import useDataEvent from "./CustomHook";
import AgentChat from "./AgentChat";
import ConfirmationPopup from "./ConfirmationPopup";
import MainStore from "./MainStore";
import { Fragment, memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Avatar, Card, CardBody, CardHeader } from "@heroui/react";
import { Table } from "./Table";
import { HeroIcon } from "./HeroIcon";
import { KonvaPrimitive } from "./KonvaPrimitive";
import FlowInstanceOutput from "./FlowInstanceOutput";
import InfiniteCanvas from "./InfiniteCanvas";
import BoardViewer from "./BoardViewer";
import { Stage, Layer } from "react-konva";
import { renderPlainObject } from "./RenderHelpers";
import { buildFieldExtractMarkdownTable } from "./SharedTransforms";
import PrimitiveConfig, { flattenStructuredResponse } from "./PrimitiveConfig";
import { uniquePrimitives } from "./MainStore";
import { buildCanvasClickHandlers } from "./utils/canvasClickHandlers";
import { useNavigate } from "react-router-dom";

const EMPTY_ARRAY = [];

const TASK_SECTIONS = [
  {
    id: "discovery",
    title: "Data discovery & enrichment",
    description: "Find, ingest, and enrich the sources we need to power the task.",
  },
  {
    id: "analysis",
    title: "Analysis",
    description: "Explore hypotheses, run workflows, and synthesize findings.",
  },
  {
    id: "presentation",
    title: "Presentation & visualization",
    description: "Shape outputs into compelling slides, charts, and narratives.",
  },
];

const MAIN_SECTION_KEYS = TASK_SECTIONS.map((section) => `section_${section.id}`);
const MAIN_SECTION_KEY_SET = new Set(MAIN_SECTION_KEYS);

const PLAN_TRACKER_EVENTS =
  "control_update set_field set_parameter parameter_update relationship_update add_primitives remove_primitives new_primitives";
const DISCOVERY_SOURCE_TYPES = new Set(["search", "query", "summarizer", "actionrunner"]);
const DISCOVERY_CANVAS_RENDER_TYPES = new Set(["search", "actionrunner", "query", "summarizer"]);
const INCLUDE_DIRECT_QUERY_DIAGNOSTIC_SUMMARIES = true;
const ARTEFACTS_CANVAS_STANDARD_WIDTH = 420;
const ARTEFACTS_CANVAS_SUMMARY_WIDTH = ARTEFACTS_CANVAS_STANDARD_WIDTH * 3;
const OUTPUT_ENTITY_TYPES = new Set(["entity", "result"]);
const REMOTE_PREVIEW_REFERENCE_IDS = new Set([34, 78]);
const PANE_TRANSITION_DURATION = 0.28;
const PANE_TRANSITION = `${PANE_TRANSITION_DURATION}s ease`;
const FEED_RELATION_PREFIXES = [
  "primitives.origin",
  "primitives.source",
  "primitives.auto",
  "primitives.link",
  "primitives.result",
  "primitives.results",
  "primitives.inputs",
  "primitives.input",
  "primitives.imports",
  "primitives.axis",
];
const GLOBAL_CONTEXT_DISPLAY_FIELDS = [
  { key: "marketType", label: "Market type" },
  { key: "buyerSegment", label: "Buyer segment" },
  { key: "marketScope", label: "Market scope" },
  { key: "focusDimension", label: "Focus" },
  { key: "meetingSignalTopics", label: "Meeting signal topics" },
  { key: "meetingPeople", label: "Meeting people" },
  { key: "stakeholderRoles", label: "Stakeholder roles" },
  { key: "focusBusinessUnit", label: "Business unit" },
  { key: "focusProductLine", label: "Product line" },
  { key: "focusMarketSegment", label: "Market segment" },
  { key: "focusRegion", label: "Region" },
  { key: "focusCustomerType", label: "Customer type" },
  { key: "focusRoles", label: "Focus roles" },
  { key: "focusStrict", label: "Strict focus" },
];

function buildSubChatSummary(history = []) {
  if (!Array.isArray(history) || history.length === 0) {
    return null;
  }
  const recentUsers = history
    .filter((entry) => entry?.role === "user" && !entry.hidden && typeof entry.content === "string")
    .map((entry) => entry.content.trim())
    .filter(Boolean)
    .slice(-2);
  const recentAssistant =
    history
      .slice()
      .reverse()
      .find((entry) => entry?.role === "assistant" && !entry.hidden && typeof entry.content === "string")?.content ?? "";
  if (!recentUsers.length && !recentAssistant) {
    return null;
  }
  const parts = [];
  if (recentUsers.length) {
    parts.push(`User requests: ${recentUsers.join(" / ")}`);
  }
  if (recentAssistant) {
    parts.push(`Latest response: ${recentAssistant.trim()}`);
  }
  return `Focused output recap — ${parts.join(" | ")}`;
}

function resolveEvidenceLabel(primitiveItem) {
  if (!primitiveItem) {
    return "Item";
  }
  const label =
    primitiveItem.metadata?.title ||
    primitiveItem.referenceParameters?.title ||
    primitiveItem.referenceParameters?.name ||
    primitiveItem.title ||
    primitiveItem.type ||
    "Item";
  return String(label).trim() || "Item";
}

function formatUpdatedLabel(value) {
  if (!value) {
    return "—";
  }
  const ts = new Date(value);
  if (Number.isNaN(ts.getTime())) {
    return "—";
  }
  return ts.toLocaleString();
}

function formatAdvisorTypeLabel(type) {
  if (type === "gap_fill_request") {
    return "Data needed";
  }
  if (type === "analysis_ready") {
    return "Analysis ready";
  }
  return "Advisor";
}

function coerceContextText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return null;
}

function coerceContextList(value) {
  const list = Array.isArray(value) ? value : value == null ? [] : [value];
  const normalized = list
    .map((entry) => coerceContextText(entry))
    .filter(Boolean);
  return normalized.length ? normalized : null;
}

function coerceContextBoolean(value) {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
}

function extractGlobalContextFromPlanReference(reference = null) {
  const planRef = reference && typeof reference === "object" ? reference : {};
  const anchor = planRef.anchor ?? planRef.anchorContext ?? {};
  return {
    marketType: coerceContextText(planRef.marketType ?? planRef.market_type ?? anchor.marketType ?? anchor.market_type),
    buyerSegment: coerceContextText(
      planRef.buyerSegment ?? planRef.buyer_segment ?? anchor.buyerSegment ?? anchor.buyer_segment,
    ),
    marketScope: coerceContextText(planRef.marketScope ?? planRef.market_scope ?? planRef.marketContext),
    focusDimension: coerceContextText(planRef.focusDimension ?? planRef.focus_dimension ?? planRef.focus),
    meetingSignalTopics: coerceContextList(
      planRef.meetingSignalTopics ??
        planRef.meeting_signal_topics ??
        anchor.meetingSignalTopics ??
        anchor.meeting_signal_topics,
    ),
    meetingPeople: coerceContextList(
      planRef.meetingPeople ?? planRef.meeting_people ?? anchor.meetingPeople ?? anchor.meeting_people,
    ),
    stakeholderRoles: coerceContextList(
      planRef.stakeholderRoles ??
        planRef.stakeholder_roles ??
        anchor.stakeholderRoles ??
        anchor.stakeholder_roles,
    ),
    focusBusinessUnit: coerceContextText(
      planRef.focus_business_unit ?? planRef.focusBusinessUnit ?? anchor.focus_business_unit,
    ),
    focusProductLine: coerceContextText(
      planRef.focus_product_line ?? planRef.focusProductLine ?? anchor.focus_product_line,
    ),
    focusMarketSegment: coerceContextText(
      planRef.focus_market_segment ?? planRef.focusMarketSegment ?? anchor.focus_market_segment,
    ),
    focusRegion: coerceContextText(planRef.focus_region ?? planRef.focusRegion ?? anchor.focus_region),
    focusCustomerType: coerceContextText(
      planRef.focus_customer_type ?? planRef.focusCustomerType ?? anchor.focus_customer_type,
    ),
    focusRoles: coerceContextList(planRef.focus_roles ?? planRef.focusRoles ?? anchor.focus_roles),
    focusStrict: coerceContextBoolean(planRef.focus_strict ?? planRef.focusStrict ?? anchor.focus_strict),
  };
}

function formatGlobalContextValue(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const list = value
      .map((entry) => formatGlobalContextValue(entry))
      .filter(Boolean);
    return list.length ? list.join(", ") : null;
  }
  if (typeof value === "object") {
    const compact = JSON.stringify(value);
    return compact && compact !== "{}" ? compact : null;
  }
  return String(value);
}

function normalizeContentHints(value) {
  if (!value) {
    return [];
  }
  const list = Array.isArray(value) ? value : [value];
  return list.map((entry) => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean);
}

function resolveStepContentHints({ stepSnapshot, stepPrimitive } = {}) {
  const merged = [];
  const seen = new Set();
  const append = (value) => {
    normalizeContentHints(value).forEach((hint) => {
      const key = hint.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push(hint);
    });
  };
  append(stepSnapshot?.metadata?.contentHints);
  append(stepSnapshot?.metadata?.content_hint);
  append(stepSnapshot?.contentHints);
  append(stepPrimitive?.referenceParameters?.contentHints);
  append(stepPrimitive?.referenceParameters?.content_hint);
  return merged;
}

function hasStructuredSummaryHint(contentHints) {
  if (!Array.isArray(contentHints) || contentHints.length === 0) {
    return false;
  }
  return contentHints.some(
    (hint) => hint === "structured_summary" || hint.endsWith(".structured_summary"),
  );
}

function resolveExtractFieldValue(item, rawPath) {
  if (!rawPath || typeof rawPath !== "string") {
    return undefined;
  }
  const path = rawPath.trim();
  if (!path) {
    return undefined;
  }
  if (path === "context" || path === "primitive.context") {
    return item?.context;
  }
  const prefixResolvers = [
    { prefix: "referenceParameters.", target: item?.referenceParameters ?? {} },
    { prefix: "primitive.", target: item },
    { prefix: "data.", target: item?.data ?? {} },
  ];
  let base = item?.referenceParameters ?? {};
  let trimmedPath = path;
  for (const resolver of prefixResolvers) {
    if (path.startsWith(resolver.prefix)) {
      base = resolver.target;
      trimmedPath = path.slice(resolver.prefix.length);
      break;
    }
  }
  if (!trimmedPath) {
    return base;
  }
  const segments = trimmedPath.split(".").filter(Boolean);
  let current = base;
  for (const segment of segments) {
    if (current === undefined || current === null) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function formatExtractedFieldContent(value) {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const lines = value
      .map((entry) => {
        if (entry === undefined || entry === null) {
          return "";
        }
        if (typeof entry === "string") {
          return entry.trim();
        }
        if (typeof entry === "number" || typeof entry === "boolean") {
          return String(entry);
        }
        try {
          return JSON.stringify(entry);
        } catch (_error) {
          return String(entry);
        }
      })
      .filter((entry) => entry && entry.length > 0);
    return lines.length ? lines.join("\n") : null;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value);
}

function hasExtractedFieldContent(value) {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => hasExtractedFieldContent(entry));
  }
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return false;
    }
    return keys.some((key) => hasExtractedFieldContent(value[key]));
  }
  return String(value).trim().length > 0;
}

function buildContentMarkdownTable(items, contentHints) {
  const normalizedHints = normalizeContentHints(contentHints);
  if (normalizedHints.length === 0) {
    return null;
  }
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  const allowedReferenceIds = new Set([
    PrimitiveConfig.Constants.GENERIC_SUMMARY,
    29,
  ]);
  if (items.some((item) => !allowedReferenceIds.has(Number(item?.referenceId)))) {
    return null
  }
  const wantsStructuredSummary = hasStructuredSummaryHint(normalizedHints);
  const preparedItems = wantsStructuredSummary
    ? items.map((item) => {
        const structuredSummary = item?.referenceParameters?.structured_summary;
        if (!structuredSummary) {
          return item;
        }
        const markdown =
          typeof structuredSummary === "string"
            ? structuredSummary
            : flattenStructuredResponse(structuredSummary, structuredSummary);
        return {
          ...item,
          referenceParameters: {
            ...(item?.referenceParameters ?? {}),
            structured_summary: markdown,
          },
        };
      })
    : items;
  const isSingleItem = preparedItems.length === 1;
  if (isSingleItem) {
    const singleItem = preparedItems[0];
    const availableHints = normalizedHints
      .map((hint) => ({ hint, rawValue: resolveExtractFieldValue(singleItem, hint) }))
      .filter((entry) => hasExtractedFieldContent(entry.rawValue));
    if (availableHints.length === 1) {
      const extracted = formatExtractedFieldContent(availableHints[0].rawValue);
      if (extracted) {
        return extracted;
      }
    }
  }
  return buildFieldExtractMarkdownTable({
    items: preparedItems,
    hints: normalizedHints,
  });
}

function MarkdownTablePreview({ markdown, width = 360, height, maxHeight, className, scaleToFit = true }) {
  const stageRef = useRef();
  const [measuredHeight, setMeasuredHeight] = useState(null);
  const tableMeta = useMemo(() => {
    if (!markdown) {
      return { lineCount: 0, columnCount: 0 };
    }
    const lines = markdown.split(/\r?\n/);
    const header = lines.find((line) => line.trim().startsWith("|")) ?? "";
    const columns = header
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 0);
    const brCount = (markdown.match(/<br\s*\/?>/gi) ?? []).length;
    return { lineCount: lines.length, columnCount: columns.length, brCount };
  }, [markdown]);
  const baseColumnWidth = 110;
  const naturalWidth = Math.max(width, (tableMeta.columnCount || 1) * baseColumnWidth);
  const computedLineCount = tableMeta.lineCount + (tableMeta.brCount ?? 0);
  const resolvedMaxHeight =
    typeof maxHeight === "number" ? maxHeight : (scaleToFit ? 520 : Number.POSITIVE_INFINITY);
  const estimateLineLength = Math.max(30, Math.floor(naturalWidth / 8));
  const estimatedWrapLines =
    markdown && !scaleToFit
      ? Math.ceil(markdown.length / estimateLineLength)
      : 0;
  const estimatedLineCount = Math.max(computedLineCount, estimatedWrapLines);
  const computedHeight =
    height ??
    Math.min(
      resolvedMaxHeight,
      Math.max(
        220,
        estimatedLineCount > 0 ? estimatedLineCount * 20 + 120 : Math.round((width / 16) * 9),
      ),
    );
  const unconstrainedHeight = Math.min(12000, Math.max(4000, estimatedLineCount * 28 + 400));
  const renderHeight = scaleToFit ? computedHeight : unconstrainedHeight;
  const renderNode = useMemo(() => {
    if (!markdown) return null;
    return renderPlainObject({
      type: "text",
      text: markdown,
      width: naturalWidth,
      height: renderHeight,
      padding: [16, 16, 16, 16],
      fontSize: 12,
    });
  }, [markdown, naturalWidth, renderHeight, scaleToFit]);

  useEffect(() => {
    if (!stageRef.current || !renderNode) {
      return;
    }
    const stage = stageRef.current;
    const layer = stage.children?.[0];
    if (!layer) {
      return;
    }
    layer.destroyChildren();
    const cloned = renderNode.clone();
    layer.add(cloned);
    const scale = scaleToFit && naturalWidth > width ? width / naturalWidth : 1;
    stage.scale({ x: scale, y: scale });
    stage.width(scaleToFit ? width : naturalWidth);
    if (!scaleToFit) {
      const measured = cloned.getClientRect({ skipShadow: true, skipStroke: false });
      const nextHeight = measured?.height ? Math.ceil(measured.height + 24) : renderHeight;
      stage.height(nextHeight);
      if (measuredHeight !== nextHeight) {
        setMeasuredHeight(nextHeight);
      }
    } else {
      stage.height(renderHeight);
    }
    layer.draw();
    return () => {
      layer.destroyChildren();
    };
  }, [renderNode, naturalWidth, width, renderHeight, scaleToFit, measuredHeight]);

  if (!markdown) {
    return null;
  }

  const appliedHeight = !scaleToFit && measuredHeight ? measuredHeight : renderHeight;

  return (
    <div
      className={className ?? "inline-flex m-2 rounded-lg p-2 border"}
      style={{
        width,
        maxWidth: width,
        maxHeight: scaleToFit ? renderHeight : undefined,
        overflow: scaleToFit ? "hidden" : "visible",
      }}
    >
      <Stage ref={stageRef} width={scaleToFit ? width : naturalWidth} height={appliedHeight}>
        <Layer perfectDrawEnabled={false} listening={false}></Layer>
      </Stage>
    </div>
  );
}

function sortGroups(groups) {
  return groups.sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.title.localeCompare(b.title);
  });
}

function groupPrimitivesByType(descendants, allowedTypes) {
  if (!Array.isArray(descendants) || descendants.length === 0) {
    return [];
  }

  const seen = new Set();
  const grouped = new Map();
  for (const primitiveItem of descendants) {
    if (!primitiveItem || !allowedTypes.has(primitiveItem.type)) {
      continue;
    }
    if( primitiveItem.flowElement ){
      continue;
    }
    if( primitiveItem.type === "search" && primitiveItem.primitives.config.allIds.length ){
      continue;
    }

    if( seen.has( primitiveItem.id) ){
      continue
    }
    seen.add(primitiveItem.id)
    const key = resolveEvidenceLabel(primitiveItem);
    const entry = grouped.get(key) || { title: key, count: 0, items: [] };
    entry.count += 1;
    entry.items.push(primitiveItem);
    grouped.set(key, entry);
  }

  return sortGroups(Array.from(grouped.values()));
}

function getColumnCountForWidth(width) {
  if (!Number.isFinite(width) || width <= 0) {
    return 1;
  }
  if (width >= 1680) {
    return 3;
  }
  if (width >= 1260) {
    return 2;
  }
  return 1;
}

function chunkIntoRows(items, columns = 1) {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  const safeColumns = Math.max(1, Number(columns) || 1);
  const rows = [];
  for (let i = 0; i < items.length; i += safeColumns) {
    rows.push(items.slice(i, i + safeColumns));
  }
  return rows;
}

function normalizeNumericInput(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.length;
  }
  if (value && typeof value === "object" && typeof value.length === "number") {
    return value.length;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
}

function getFirstNumericValue(source, keys) {
  if (!source) {
    return null;
  }
  for (const key of keys) {
    if (!(key in source)) continue;
    const normalized = normalizeNumericInput(source[key]);
    if (normalized !== null) {
      return normalized;
    }
  }
  return null;
}

function getPrimitiveId(primitiveItem) {
  return primitiveItem?.id ?? primitiveItem?._id ?? primitiveItem?.plainId ?? null;
}

function shouldTrackParentRelationship(relationship) {
  if (typeof relationship !== "string") {
    return false;
  }
  if (relationship.startsWith("primitives.config") || relationship.startsWith("primitives.subfi")) {
    return false;
  }
  return FEED_RELATION_PREFIXES.some((prefix) => relationship.startsWith(prefix));
}

function extractParentPrimitiveIds(primitiveItem) {
  const parentMap = primitiveItem?._parentPrimitives ?? primitiveItem?.parentPrimitives;
  if (!parentMap || typeof parentMap !== "object") {
    return EMPTY_ARRAY;
  }
  const parentIds = [];
  Object.entries(parentMap).forEach(([parentId, relationships]) => {
    if (!parentId) {
      return;
    }
    if (!Array.isArray(relationships) || relationships.length === 0) {
      parentIds.push(parentId);
      return;
    }
    if (relationships.some(shouldTrackParentRelationship)) {
      parentIds.push(parentId);
    }
  });
  return parentIds;
}

function getPlanStepParentId(step) {
  if (!step) {
    return null;
  }
  return (
    step.parentId ??
    step.parent_id ??
    step.metadata?.parentId ??
    step.metadata?.parent_id ??
    step.metadata?.parentStepId ??
    step.metadata?.parent_step_id ??
    (Array.isArray(step.parents) && step.parents.length ? step.parents[0] : null) ??
    null
  );
}

function getPrimitiveOwnerStepId(primitiveItem) {
  if (!primitiveItem) {
    return null;
  }
  return (
    primitiveItem.stepId ??
    primitiveItem.planStepId ??
    primitiveItem.referenceParameters?.planStepId ??
    primitiveItem.referenceParameters?.stepId ??
    primitiveItem.metadata?.planStepId ??
    primitiveItem.metadata?.stepId ??
    null
  );
}

function extractParentPrimitiveMap(primitiveItem) {
  return primitiveItem?._parentPrimitives ?? primitiveItem?.parentPrimitives ?? null;
}

function resolveOwnerStepFromParents(primitiveItem, planPrimitiveIdToStepId, lookupPrimitiveById) {
  const parentMap = extractParentPrimitiveMap(primitiveItem);
  if (!parentMap) {
    return null;
  }
  const queue = Object.keys(parentMap).filter(Boolean);
  const visited = new Set(queue);
  while (queue.length) {
    const parentId = queue.shift();
    if (planPrimitiveIdToStepId.has(parentId)) {
      return planPrimitiveIdToStepId.get(parentId);
    }
    const parentPrimitive = lookupPrimitiveById(parentId);
    if (parentPrimitive) {
      const parentParents = extractParentPrimitiveIds(parentPrimitive);
      parentParents.forEach((ancestorId) => {
        if (ancestorId && !visited.has(ancestorId)) {
          visited.add(ancestorId);
          queue.push(ancestorId);
        }
      });
    }
  }
  return null;
}

function addPrimitiveAndItems(targetSet, primitive) {
  if (!primitive) {
    return;
  }
  const primitiveId = getPrimitiveId(primitive);
  if (primitiveId) {
    targetSet.add(primitiveId);
  }
  const items = primitive.itemsForProcessing ?? [];
  items.forEach((item) => {
    const itemId = getPrimitiveId(item);
    if (itemId) {
      targetSet.add(itemId);
    }
  });
  if (primitive.type === "flow" && primitive.primitives?.origin?.allFlowinstance) {
    const flowInstances = primitive.primitives.origin.allFlowinstance;
    flowInstances.forEach((instance) => addPrimitiveAndItems(targetSet, instance));
  }
}

function extractPrimitiveIdsFromExecutionState(execState) {
  if (!execState || typeof execState !== "object") {
    return EMPTY_ARRAY;
  }
  const ids = new Set();
  Object.entries(execState).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    if (key.endsWith("Id") && typeof value === "string") {
      ids.add(value);
      return;
    }
    if (key.endsWith("Ids") && Array.isArray(value)) {
      value.forEach((entry) => {
        if (typeof entry === "string") {
          ids.add(entry);
        }
      });
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (nestedKey.endsWith("Id") && typeof nestedValue === "string") {
          ids.add(nestedValue);
        }
        if (nestedKey.endsWith("Ids") && Array.isArray(nestedValue)) {
          nestedValue.forEach((entry) => {
            if (typeof entry === "string") {
              ids.add(entry);
            }
          });
        }
      });
    }
  });
  return Array.from(ids);
}

function resolveOutputPrimitivesFromExecutionState(execState, mainstore) {
  const extractOutputsFromPrimitive = (primitive) => {
    if (!primitive) {
      return [];
    }
    const outputs = [];
    const seen = new Set();
    const push = (item) => {
      const id = getPrimitiveId(item);
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      outputs.push(item);
    };
    if (primitive.type === "flow") {
      (primitive.primitives?.origin?.allFlowinstance ?? []).forEach(push);
      return outputs;
    }
    if (primitive.type === "view" || primitive.type === "flowinstance") {
      push(primitive);
      return outputs;
    }
    if (Array.isArray(primitive.itemsForProcessing) && primitive.itemsForProcessing.length) {
      primitive.itemsForProcessing.forEach(push);
    }
    return outputs;
  };

  const prioritizedIdGroups = [
    [execState?.outputViewId ?? null],
    [execState?.primaryPrimitiveId ?? null],
    [
      execState?.profileViewId ?? null,
      execState?.runner?.context?.outputViewId ?? null,
      execState?.runner?.context?.viewId ?? null,
      execState?.runner?.targetPrimitiveId ?? null,
      execState?.runnerPrimitiveId ?? null,
      ...extractPrimitiveIdsFromExecutionState(execState),
    ],
  ];

  for (const idGroup of prioritizedIdGroups) {
    const candidateIds = Array.from(new Set((idGroup ?? []).filter(Boolean)));
    if (!candidateIds.length) {
      continue;
    }
    const candidatePrimitives = candidateIds
      .map((id) => mainstore.primitive(id))
      .filter(Boolean);
    for (const primitive of candidatePrimitives) {
      const outputs = extractOutputsFromPrimitive(primitive);
      if (outputs.length) {
        return outputs;
      }
    }
  }
  return [];
}

function formatPrimitiveDisplayTitle(primitiveItem) {
    const base = primitiveItem?.title ||
        primitiveItem?.metadata?.title ||
        primitiveItem?.displayType ||
        primitiveItem?.type
    return primitiveItem.inFlow && primitiveItem.origin?.title ? primitiveItem.origin.title + " - " + base : base
}

function getRelevantChildren( primitiveItem){
  const children = primitiveItem?.primitives?.origin.allItems 
  if( children.length === 0){
    return []
  }
  return children.filter(d=>d.type !== "segment")

}

function getDomainFromUrl(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const normalized = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    const url = new URL(normalized);
    return url.hostname.replace(/^www\./i, "");
  } catch {
    const match = trimmed.match(/([a-z0-9.-]+\.[a-z]{2,})/i);
    return match ? match[1].replace(/^www\./i, "").toLowerCase() : null;
  }
}

function resolveAbsoluteUrl(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

function resolveCompanyLogoUrl(item) {
  if (!item) {
    return null;
  }
  const domainCandidates = [
    item.metadata?.domain,
    item.referenceParameters?.domain,
    getDomainFromUrl(item.referenceParameters?.url),
    getDomainFromUrl(item.referenceParameters?.website),
    getDomainFromUrl(item.metadata?.url),
  ].filter(Boolean);
  if (domainCandidates.length > 0) {
    return `/api/companyLogo?domain=${encodeURIComponent(domainCandidates[0])}`;
  }
  const label = item.metadata?.title || item.metadata?.name || item.title;
  return label ? `/api/companyLogo?name=${encodeURIComponent(label)}` : null;
}

function resolvePreviewAvatarUrl(item) {
  if (!item) {
    return undefined;
  }
  if (REMOTE_PREVIEW_REFERENCE_IDS.has(item.referenceId)) {
    const remoteCandidates = [
      item.referenceParameters?.url,
      item.referenceParameters?.link,
      item.referenceParameters?.sourceUrl,
      item.referenceParameters?.source_url,
      item.referenceParameters?.articleUrl,
      item.referenceParameters?.article_url,
      item.referenceParameters?.storyUrl,
      item.referenceParameters?.story_url,
      item.metadata?.url,
      item.metadata?.link,
      item.metadata?.sourceUrl,
      item.metadata?.source_url,
      item.url,
    ];
    const remoteSource = remoteCandidates.map(resolveAbsoluteUrl).find(Boolean);
    if (remoteSource) {
      return `/api/preview?url=${encodeURIComponent(remoteSource)}`;
    }
  }
  if (item.referenceId === 29) {
    const companyLogoUrl = resolveCompanyLogoUrl(item);
    if (companyLogoUrl) {
      return companyLogoUrl;
    }
  }
  const primitiveId = item.id ?? item._id ?? item.plainId;
  if (!primitiveId) {
    return undefined;
  }
  const suffix = item.imageCount ? `?${item.imageCount}` : "";
  return `/api/image/${primitiveId}${suffix}`;
}

function formatDiscoveryStatus(primitiveItem) {
  const params = primitiveItem?.referenceParameters ?? {};
  const recordCount = normalizeNumericInput( getRelevantChildren(primitiveItem) )

  if (recordCount !== null && recordCount !== undefined) {
    return recordCount.toLocaleString();
  }

  const progressRaw = getFirstNumericValue(params, [
    "progress",
    "progressPercent",
    "progress_percent",
    "percentComplete",
    "percent_complete",
  ]);
  if (progressRaw !== null && progressRaw !== undefined) {
    const pct = progressRaw > 1 ? progressRaw : progressRaw * 100;
    return `${Math.round(pct)}% ready`;
  }

  const statusText =
    params.status ??
    params.state ??
    primitiveItem?.metadata?.status ??
    primitiveItem?.status;

  if (statusText) {
    return statusText;
  }

  return "Idle";
}

function isPrimitiveActive(primitiveItem) {
  const executionState =
    primitiveItem?.referenceParameters?.executionState ??
    primitiveItem?.executionState ??
    null;
  const runnerStatus = executionState?.runner?.status ?? null;
  const executionStatus = executionState?.status ?? null;
  if (
    runnerStatus === "failed" ||
    executionStatus === "error" ||
    executionStatus === "blocked"
  ) {
    return false;
  }

  const processing = primitiveItem?.processing ?? {};
  const queryStatus = processing.query?.status;
  const flowStatus = processing.flow?.status;
  const runStatus = processing.run_step?.status;
  const bdStatus = processing.bd ?? null;
  let brightdataActive = false;
  if (bdStatus && typeof bdStatus === "object") {
    Object.values(bdStatus).some((entry) => {
      if (!entry) return false;
      const status =
        (typeof entry?.status === "string" && entry.status) ||
        (typeof entry?.status?.status === "string" && entry.status.status) ||
        (typeof entry === "string" && entry) ||
        null;
      if (!status) return false;
      const normalized = status.toLowerCase();
      if (normalized === "pending" || normalized === "running") {
        brightdataActive = true;
        return true;
      }
      return false;
    });
  }

  return (
    queryStatus === "running" ||
    flowStatus === "running" ||
    runStatus === "running" ||
    queryStatus === "pending" ||
    flowStatus === "pending" ||
    runStatus === "pending" ||
    brightdataActive
  );
}

export default function AnalysisPage({ primitive, ...props }) {
  const navigate = useNavigate();
  const [, setAgentStatus] = useState({ activeChat: false });
  const [activeTaskArea, setActiveTaskArea] = useState("discovery");
  const [planPrimitiveId, setPlanPrimitiveId] = useState(null);
  const [advisorSuggestionLog, setAdvisorSuggestionLog] = useState([]);
  const [planStatsVersion, bumpPlanStatsVersion] = useReducer((count) => count + 1, 0);
  const [discoveryGroups, setDiscoveryGroups] = useState([]);
  const [evidenceGroups, setEvidenceGroups] = useState([]);
  const [expandedSections, setExpandedSections] = useState(() => new Set(["section_discovery"]));
  const [focusedPlanStepId, setFocusedPlanStepId] = useState(null);
  const [focusedPlanStepIds, setFocusedPlanStepIds] = useState(null);
  const handlePlanStepFocus = useCallback((focusPayload) => {
    if (!focusPayload) {
      setFocusedPlanStepId(null);
      setFocusedPlanStepIds(null);
      return;
    }
    if (typeof focusPayload === "string") {
      setFocusedPlanStepId(focusPayload);
      setFocusedPlanStepIds(null);
      return;
    }
    if (typeof focusPayload === "object") {
      const stepIds = Array.isArray(focusPayload.stepIds)
        ? focusPayload.stepIds.filter(Boolean).map((stepId) => String(stepId))
        : [];
      if (!stepIds.length) {
        setFocusedPlanStepId(null);
        setFocusedPlanStepIds(null);
        return;
      }
      const anchorId = focusPayload.anchorStepId
        ? String(focusPayload.anchorStepId)
        : stepIds[0];
      setFocusedPlanStepId(anchorId ?? null);
      setFocusedPlanStepIds(stepIds);
      return;
    }
    setFocusedPlanStepId(null);
    setFocusedPlanStepIds(null);
  }, []);
  const [discoveryActiveOnly, setDiscoveryActiveOnly] = useState(true);
  const [analysisLeafOnly, setAnalysisLeafOnly] = useState(true);
  const [outputCarouselIndexMap, setOutputCarouselIndexMap] = useState({});
  const [rightPaneWidth, setRightPaneWidth] = useState(() =>
    typeof window !== "undefined" ? Math.max(window.innerWidth * 0.5, 720) : 960,
  );
  const [discoveryPaneCollapsed, setDiscoveryPaneCollapsed] = useState(false);
  const [selectedDataGroup, setSelectedDataGroup] = useState(null);
  const [showAdvisorDropdown, setShowAdvisorDropdown] = useState(false);
  const [selectedOutputView, setSelectedOutputView] = useState(null);
  const [selectedWorkstreamId, setSelectedWorkstreamId] = useState(null);
  const [workstreamChatPrimitive, setWorkstreamChatPrimitive] = useState(null);
  const [subChatOwner, setSubChatOwner] = useState(null);
  const [subChatSessionInfo, setSubChatSessionInfo] = useState(null);
  const [workstreamListCollapsed, setWorkstreamListCollapsed] = useState(true);
  const [globalContextCollapsed, setGlobalContextCollapsed] = useState(true);
  const [showArchivedGoals, setShowArchivedGoals] = useState(false);
  const [showGoalDeleteConfirm, setShowGoalDeleteConfirm] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [deletingGoal, setDeletingGoal] = useState(false);
  const [artefactsViewMode, setArtefactsViewMode] = useState("card");
  const workspaceId = primitive?.workspaceId ?? null;
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  const mainAgentRef = useRef(null);
  const advisorDropdownRef = useRef(null);
  const workstreamChatCreateRef = useRef(new Set());
  const suppressAutoWorkstreamSelectRef = useRef(false);
  const [rightPaneNode, setRightPaneNode] = useState(null);
  const handleRightPaneRef = useCallback((node) => {
    setRightPaneNode(node || null);
  }, []);
  const mainstore = useMemo(() => MainStore(), []);
  const handleOpenWorkspace = useCallback(
    (primitive) => {
      if (!primitive) {
        return;
      }
      mainstore.sidebarSelect?.(primitive);
    },
    [mainstore],
  );
  const handleOutputCardSelect = useCallback((entry, primitive) => {
    if (!entry || !primitive) {
      return;
    }
    setSelectedOutputView({
      entry,
      primitive,
    });
    setSubChatOwner("output");
  }, []);
  const handleCloseOutputView = useCallback(() => {
    setSelectedOutputView(null);
    setSubChatOwner(selectedWorkstreamId ? "workstream" : null);
  }, [selectedWorkstreamId]);
  const handleWorkstreamSelect = useCallback((workstreamId) => {
    setSelectedOutputView(null);
    setSelectedWorkstreamId(workstreamId ?? null);
    if (workstreamId) {
      setSubChatOwner("workstream");
    }
  }, []);
  const handleCloseWorkstreamChat = useCallback(() => {
    suppressAutoWorkstreamSelectRef.current = true;
    setSelectedWorkstreamId(null);
    setWorkstreamChatPrimitive(null);
    setSubChatOwner(null);
  }, []);
  const handleAdvisorSuggestionsChange = useCallback((currentSuggestions = []) => {
    const deduped = [];
    const seen = new Set();
    currentSuggestions.forEach((entry) => {
      if (!entry?.id) {
        return;
      }
      if (seen.has(entry.id)) {
        return;
      }
      seen.add(entry.id);
      deduped.push(entry);
    });
    setAdvisorSuggestionLog(deduped);
  }, []);
  const appendMessageToChat = useCallback(
    async (chatId, entry) => {
      if (!chatId || !entry) {
        return;
      }
      let chatPrimitive = mainstore.primitive(chatId);
      if (!chatPrimitive && typeof mainstore.fetchPrimitive === "function") {
        try {
          await mainstore.fetchPrimitive(chatId);
          chatPrimitive = mainstore.primitive(chatId);
        } catch (error) {
          console.warn("Failed to load chat for summary", error);
        }
      }
      if (!chatPrimitive || typeof chatPrimitive.setField !== "function") {
        return;
      }
      const existingHistory = Array.isArray(chatPrimitive.referenceParameters?.chat_history)
        ? [...chatPrimitive.referenceParameters.chat_history]
        : [];
      existingHistory.push(entry);
      try {
        await chatPrimitive.setField("referenceParameters.chat_history", existingHistory);
      } catch (error) {
        console.warn("Failed to append chat summary", error);
      }
    },
    [mainstore],
  );
  const summarizeSubChatSession = useCallback(
    async (sessionInfo) => {
      if (!sessionInfo?.id) {
        return;
      }
      let subChatPrimitive = mainstore.primitive(sessionInfo.id);
      if (!subChatPrimitive && typeof mainstore.fetchPrimitive === "function") {
        try {
          await mainstore.fetchPrimitive(sessionInfo.id);
          subChatPrimitive = mainstore.primitive(sessionInfo.id);
        } catch (error) {
          console.warn("Failed to load sub-chat for summary", error);
        }
      }
      if (!subChatPrimitive) {
        return;
      }
      const history = subChatPrimitive.referenceParameters?.chat_history ?? [];
      const summary = buildSubChatSummary(history);
      if (!summary) {
        return;
      }
      const mainChatInfo = mainAgentRef.current?.getActiveChatInfo?.();
      if (!mainChatInfo?.id) {
        return;
      }
      await appendMessageToChat(mainChatInfo.id, {
        role: "assistant",
        content: summary,
        metadata: {
          subChatSummary: true,
          sourceChatId: sessionInfo.id,
        },
      });
      await appendMessageToChat(sessionInfo.id, {
        role: "assistant",
        content: "Shared a recap with the main chat.",
        hidden: true,
        metadata: { subChatSummary: true },
      });
    },
    [appendMessageToChat, mainstore],
  );

  const planPrimitive = planPrimitiveId ? mainstore.primitive(planPrimitiveId) : null;
  const workstreamPrimitives = useMemo(() => {
    if (!planPrimitive) {
      return [];
    }
    const direct = planPrimitive.primitives?.allWorkstream;
    if (!Array.isArray(direct)) {
      return [];
    }
    const byKey = new Map();
    direct.filter(Boolean).forEach((workstream) => {
      const id = getPrimitiveId(workstream);
      const goalId =
        workstream?.referenceParameters?.goalId ??
        workstream?.referenceParameters?.metadata?.goalId ??
        null;
      const key = goalId ? `goal:${goalId}` : id ? `id:${id}` : null;
      if (!key) {
        return;
      }
      if (!byKey.has(key)) {
        byKey.set(key, workstream);
      }
    });
    return Array.from(byKey.values());
  }, [planPrimitive, planStatsVersion]);
  const visibleWorkstreamPrimitives = useMemo(() => {
    if (showArchivedGoals) {
      return workstreamPrimitives;
    }
    return workstreamPrimitives.filter((workstream) => {
      const status = String(workstream?.referenceParameters?.status ?? "").toLowerCase();
      return status !== "archived";
    });
  }, [workstreamPrimitives, showArchivedGoals]);
  const workstreamById = useMemo(() => {
    const map = new Map();
    visibleWorkstreamPrimitives.forEach((workstream) => {
      const id = getPrimitiveId(workstream);
      if (id) {
        map.set(id, workstream);
      }
    });
    return map;
  }, [visibleWorkstreamPrimitives]);
  const selectedWorkstream = useMemo(() => {
    if (!selectedWorkstreamId) {
      return null;
    }
    return workstreamById.get(selectedWorkstreamId) ?? null;
  }, [selectedWorkstreamId, workstreamById]);
  const selectedWorkstreamParent = useMemo(() => {
    if (!selectedWorkstream) {
      return null;
    }
    return (
      (selectedWorkstream?.primitives?.origin?.allWorkstream ?? []).find(
        (parent) => parent?.type === "workstream"
      ) ?? null
    );
  }, [selectedWorkstream]);
  const handlePromoteOutput = useCallback(
    async (outputPrimitive) => {
      if (!selectedWorkstreamParent || !outputPrimitive) {
        return;
      }
      try {
        selectedWorkstreamParent.addRelationship(outputPrimitive, "content");
        await mainstore.fetchPrimitive(selectedWorkstreamParent.id, { force: true });
      } catch (error) {
        console.warn("Failed to promote output", error);
      }
    },
    [mainstore, selectedWorkstreamParent],
  );
  const planStepSnapshots = planPrimitive?.referenceParameters?.plan_steps ?? EMPTY_ARRAY;
  const definedSteps = planPrimitive?.primitives?.allStep ?? EMPTY_ARRAY;
  const reduceStepSnapshot = useCallback((step, idx) => {
    if (!step) {
      return null;
    }
    const base = {
      id: step.id ?? null,
      name: step.name ?? null,
      description: step.description ?? "",
      area: step.area ?? null,
      order: Number.isFinite(step.order) ? step.order : idx,
      status: step?.runtime?.status ?? null,
      selection: step.selection ?? null,
      mode: step.mode ?? null,
      optional: Boolean(step.optional),
      selectedActionId: step.selectedActionId ?? null,
    };
    if (Array.isArray(step.inputs) && step.inputs.length) {
      base.inputs = step.inputs.map((input) => ({
        id: input.id ?? null,
        stepId: input.stepId ?? null,
        description: input.description ?? null,
      }));
    }
    if (step.metadata) {
      const allowedMetadata = {
        optionId: step.metadata.optionId ?? null,
        origin: step.metadata.origin ?? null,
        kind: step.metadata.kind ?? null,
        subChat: step.metadata.subChat ?? null,
        requestedByStepId: step.metadata.requestedByStepId ?? null,
        parentStepId: step.metadata.parentStepId ?? null,
        sourceStepId: step.metadata.sourceStepId ?? null,
      };
      base.metadata = allowedMetadata;
    }
    if (step.executionState) {
      base.executionState = {
        status: step.executionState.status ?? null,
        awaitingApproval:
          step.executionState.awaitingApproval !== undefined
            ? step.executionState.awaitingApproval
            : undefined,
        userApproved:
          step.executionState.userApproved !== undefined ? step.executionState.userApproved : undefined,
        requestedByStepId: step.executionState.requestedByStepId ?? null,
        parentStepId: step.executionState.parentStepId ?? null,
        sourceStepId: step.executionState.sourceStepId ?? null,
      };
    }
    return base;
  }, []);
  const minimalPlanReference = useMemo(() => {
    if (!planPrimitive?.referenceParameters) {
      return null;
    }
    const { plan_steps, chat_history, ...rest } = planPrimitive.referenceParameters;
    return { ...rest };
  }, [planPrimitive, planStatsVersion]);
  const globalContextState = useMemo(() => {
    const derivedGlobalContext = extractGlobalContextFromPlanReference(minimalPlanReference);
    const explicitGlobalContext =
      minimalPlanReference?.global_context ?? minimalPlanReference?.globalContext ?? null;
    if (explicitGlobalContext && typeof explicitGlobalContext === "object" && !Array.isArray(explicitGlobalContext)) {
      return {
        ...derivedGlobalContext,
        ...explicitGlobalContext,
      };
    }
    return derivedGlobalContext;
  }, [minimalPlanReference]);
  const globalContextEntries = useMemo(() => {
    if (!globalContextState || typeof globalContextState !== "object") {
      return [];
    }
    const seen = new Set();
    const preferred = GLOBAL_CONTEXT_DISPLAY_FIELDS.map((field) => {
      const value = formatGlobalContextValue(globalContextState[field.key]);
      seen.add(field.key);
      return value ? { key: field.key, label: field.label, value } : null;
    }).filter(Boolean);
    const additional = Object.entries(globalContextState)
      .filter(([key]) => !seen.has(key))
      .map(([key, rawValue]) => {
        const value = formatGlobalContextValue(rawValue);
        if (!value) {
          return null;
        }
        const label = key
          .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
          .replace(/_/g, " ")
          .trim();
        return {
          key,
          label: label.length ? label[0].toUpperCase() + label.slice(1) : key,
          value,
        };
      })
      .filter(Boolean);
    return [...preferred, ...additional];
  }, [globalContextState]);
  const scopedPlanReference = useMemo(() => {
    if (!planPrimitive?.referenceParameters) {
      return null;
    }
    const { chat_history, ...rest } = planPrimitive.referenceParameters;
    const scopedSteps = Array.isArray(planStepSnapshots)
      ? planStepSnapshots.map(reduceStepSnapshot).filter(Boolean)
      : [];
    return {
      ...rest,
      plan_steps: scopedSteps,
    };
  }, [planPrimitive, planStepSnapshots, reduceStepSnapshot, planStatsVersion]);
  const planSnapshotForScope = useMemo(() => {
    if (!planPrimitive || !planStepSnapshots.length) {
      return null;
    }
    const ref = planPrimitive.referenceParameters ?? {};
    return {
      id: planPrimitive.id ?? planPrimitive._id ?? null,
      title: ref.title ?? planPrimitive.title ?? null,
      summary: ref.summary ?? null,
      recipeId: ref.recipeId ?? ref.recipe_id ?? null,
      status: ref.status ?? null,
      area: ref.area ?? null,
      focus: ref.focus ?? ref.focus_dimension ?? ref.focusDimension ?? null,
      steps: planStepSnapshots.map(reduceStepSnapshot).filter(Boolean),
    };
  }, [planPrimitive, planStepSnapshots, reduceStepSnapshot, planStatsVersion]);
  const workstreamRows = useMemo(() => {
    if (!visibleWorkstreamPrimitives.length) {
      return [];
    }
    const byId = new Map();
    visibleWorkstreamPrimitives.forEach((workstream) => {
      const id = getPrimitiveId(workstream);
      if (id) {
        byId.set(id, workstream);
      }
    });
    const children = new Map();
    const roots = [];
    visibleWorkstreamPrimitives.forEach((workstream) => {
      const id = getPrimitiveId(workstream);
      if (!id) {
        return;
      }
      const parent = (workstream?.primitives?.origin?.allWorkstream ?? []).find(
        (candidate) => candidate?.type === "workstream"
      );
      if (parent?.id && byId.has(parent.id)) {
        if (!children.has(parent.id)) {
          children.set(parent.id, []);
        }
        children.get(parent.id).push(workstream);
      } else {
        roots.push(workstream);
      }
    });
    const rows = [];
    const pushRows = (items, depth) => {
      items.forEach((item) => {
        rows.push({ workstream: item, depth });
        const id = getPrimitiveId(item);
        const childItems = id ? children.get(id) : null;
        if (childItems?.length) {
          pushRows(childItems, depth + 1);
        }
      });
    };
    pushRows(roots, 0);
    return rows;
  }, [visibleWorkstreamPrimitives]);
  useEffect(() => {
    setAdvisorSuggestionLog([]);
  }, [planPrimitiveId]);

  const handleDeleteGoal = useCallback(async () => {
    if (!goalToDelete?.workstreamId || deletingGoal) {
      return;
    }
    setDeletingGoal(true);
    try {
      const workstreamPrimitive = mainstore.primitive(goalToDelete.workstreamId);
      if (!workstreamPrimitive) {
        throw new Error("Workstream not found");
      }
      const goalIdToDelete = goalToDelete.goalId ?? goalToDelete.id ?? null;
      if (planPrimitiveId && goalIdToDelete) {
        const response = await fetch(`/api/plan/${planPrimitiveId}/goals/${goalIdToDelete}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || "Failed to delete goal");
        }
        if (typeof mainstore.removePrimitive === "function") {
          await mainstore.removePrimitive(workstreamPrimitive);
        }
      }
      if (goalToDelete.workstreamId === selectedWorkstreamId) {
        setSelectedWorkstreamId(null);
        setSubChatOwner(null);
      }
      if (planPrimitiveId && typeof mainstore.fetchPrimitive === "function") {
        await mainstore.fetchPrimitive(planPrimitiveId, { force: true });
      }
      bumpPlanStatsVersion();
    } catch (error) {
      console.warn("Failed to delete goal workstream", error);
    } finally {
      setDeletingGoal(false);
      setShowGoalDeleteConfirm(false);
      setGoalToDelete(null);
    }
  }, [planPrimitiveId, goalToDelete, deletingGoal, mainstore, selectedWorkstreamId]);

  useEffect(() => {
    if (suppressAutoWorkstreamSelectRef.current) {
      suppressAutoWorkstreamSelectRef.current = false;
      return;
    }
    if (selectedWorkstreamId && workstreamById.has(selectedWorkstreamId)) {
      return;
    }
    setSelectedWorkstreamId(null);
  }, [selectedWorkstreamId, workstreamById]);

  useEffect(() => {
    if (subChatOwner !== "workstream" || !selectedWorkstreamId) {
      setWorkstreamChatPrimitive(null);
      return;
    }
    const workstream = workstreamById.get(selectedWorkstreamId);
    if (!workstream) {
      setWorkstreamChatPrimitive(null);
      return;
    }
    const existingChat = (() => {
      const origin = workstream?.primitives?.origin;
      if (!origin) {
        return null;
      }
      const direct = origin.allChat;
      if (Array.isArray(direct) && direct.length) {
        return direct[0] ?? null;
      }
      const items = origin.allItems ?? [];
      if (Array.isArray(items) && items.length) {
        return items.find((item) => item?.type === "chat") ?? null;
      }
      return null;
    })();
    if (existingChat) {
      setWorkstreamChatPrimitive(existingChat);
      return;
    }
    const workstreamId = getPrimitiveId(workstream);
    if (!workstreamId) {
      setWorkstreamChatPrimitive(null);
      return;
    }
    const workstreamGoalId =
      workstream?.referenceParameters?.goalId ??
      workstream?.referenceParameters?.metadata?.goalId ??
      null;
    if (workstreamChatCreateRef.current.has(workstreamId)) {
      return;
    }
    let cancelled = false;
    const createChat = async () => {
      workstreamChatCreateRef.current.add(workstreamId);
      try {
        const title = workstream?.title ?? workstream?.referenceParameters?.title ?? "Workstream";
        const created = await mainstore.createPrimitive({
          type: "chat",
          title: `${title} chat`,
          parent: workstream,
          workspaceId: workstream?.workspaceId ?? workspaceId ?? undefined,
          referenceParameters: {
            createdAt: new Date().toISOString(),
            planPrimitiveId: planPrimitiveId ?? null,
            workstreamId,
            goalId: workstreamGoalId,
          },
        });
        if (!cancelled) {
          const chatPrimitive = created?.id ? mainstore.primitive(created.id) : null;
          setWorkstreamChatPrimitive(chatPrimitive ?? null);
          if (workstream?.id) {
            await mainstore.fetchPrimitive(workstream.id, { force: true });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setWorkstreamChatPrimitive(null);
        }
      } finally {
        workstreamChatCreateRef.current.delete(workstreamId);
      }
    };
    createChat();
    return () => {
      cancelled = true;
    };
  }, [mainstore, selectedWorkstreamId, subChatOwner, workstreamById, workspaceId]);

  useEffect(() => {
    function handleGlobalClick(event) {
      if (!advisorDropdownRef.current) {
        return;
      }
      if (!advisorDropdownRef.current.contains(event.target)) {
        setShowAdvisorDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleGlobalClick);
    return () => {
      document.removeEventListener("mousedown", handleGlobalClick);
    };
  }, []);

  useEffect(() => {
    if (!advisorSuggestionLog.length) {
      setShowAdvisorDropdown(false);
    }
  }, [advisorSuggestionLog.length]);

  const outputSteps = useMemo(() => {
    if (!Array.isArray(planStepSnapshots) || !planStepSnapshots.length) {
      return [];
    }
    const stepPrimitiveById = new Map(
      (Array.isArray(definedSteps) ? definedSteps : [])
        .filter((stepPrimitive) => stepPrimitive?.referenceParameters?.stepId)
        .map((stepPrimitive) => [stepPrimitive.referenceParameters.stepId, stepPrimitive]),
    );
    const seen = new Map();
    for (const step of planStepSnapshots) {
      if (!step?.id) {
        continue;
      }
      const stepPrimitive = stepPrimitiveById.get(step.id) ?? null;
      const hasContentHints = resolveStepContentHints({ stepSnapshot: step, stepPrimitive }).length > 0;
      if (!["analysis", "presentation"].includes(step.area) && !hasContentHints) {
        continue;
      }
      const execState = step.executionState || {};
      const outputs = resolveOutputPrimitivesFromExecutionState(execState, mainstore);
      if (!outputs?.length) {
        continue;
      }
      seen.set(step.id, {
        stepId: step.id,
        name: step.name ?? "Analysis output",
        description: step.description ?? null,
        area: step.area,
        status: step?.runtime?.status ?? null,
        order: typeof step.order === "number" ? step.order : null,
        metadata: step.metadata ?? null,
        outputs: Array.from(outputs),
      });
    }
    return Array.from(seen.values());
  }, [mainstore, planStepSnapshots, definedSteps]);
  const outputStepsById = useMemo(() => {
    const map = new Map();
    outputSteps.forEach((entry) => {
      if (entry?.stepId) {
        map.set(entry.stepId, entry.outputs ?? EMPTY_ARRAY);
      }
    });
    return map;
  }, [outputSteps]);

  const nestedDiscoveryNodes = definedSteps.flatMap(
    (step) =>
      step?.primitives?.descendants?.filter((child) => child?.type === "query" || child?.type === "search") ?? []
  );
  const watchIds = Array.from(
    new Set([
      planPrimitiveId,
      ...definedSteps.map((step) => step?.id),
      ...nestedDiscoveryNodes.map((node) => node?.id),
    ])
  ).filter(Boolean);

  useDataEvent("relationship_update", primitive.id);
  useDataEvent(planPrimitiveId ? PLAN_TRACKER_EVENTS : null, watchIds.length ? watchIds : undefined, bumpPlanStatsVersion);

  useEffect(() => {
    if (!planPrimitiveId || !planPrimitive) {
      setDiscoveryGroups([]);
      setEvidenceGroups([]);
      return;
    }

    const descendants = planPrimitive.primitives?.descendants ?? [];
    if (!Array.isArray(descendants) || descendants.length === 0) {
      setDiscoveryGroups([]);
      setEvidenceGroups([]);
      return;
    }

    const discoveryStats = groupPrimitivesByType(descendants, DISCOVERY_SOURCE_TYPES);
    const evidenceStats = groupPrimitivesByType(descendants, OUTPUT_ENTITY_TYPES);

    setDiscoveryGroups(discoveryStats);
    setEvidenceGroups(evidenceStats);
  }, [planPrimitiveId, planStatsVersion, planPrimitive]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      const isMainSection = MAIN_SECTION_KEY_SET.has(sectionId);
      if (next.has(sectionId)) {
        if (isMainSection) {
          const expandedMainCount = Array.from(next).filter((key) => MAIN_SECTION_KEY_SET.has(key)).length;
          if (expandedMainCount <= 1) {
            return prev;
          }
        }
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const handleDiscoveryActiveToggle = useCallback(() => {
    setDiscoveryActiveOnly((prev) => !prev);
  }, []);
  const handleAnalysisLeafToggle = useCallback(() => {
    setAnalysisLeafOnly((prev) => !prev);
  }, []);
  const planStepIndexLookup = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step, idx) => {
      if (step?.id) {
        map.set(step.id, idx);
      }
    });
    return map;
  }, [planStepSnapshots]);
  const planStepSnapshotById = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step) => {
      if (step?.id) {
        map.set(step.id, step);
      }
    });
    return map;
  }, [planStepSnapshots]);
  useEffect(() => {
    if (!focusedPlanStepId) {
      if (focusedPlanStepIds) {
        setFocusedPlanStepIds(null);
      }
      return;
    }
    if (!planStepSnapshotById.has(focusedPlanStepId)) {
      setFocusedPlanStepId(null);
      setFocusedPlanStepIds(null);
      return;
    }
    if (focusedPlanStepIds?.length) {
      const validIds = focusedPlanStepIds.filter((stepId) => planStepSnapshotById.has(stepId));
      if (!validIds.length) {
        setFocusedPlanStepId(null);
        setFocusedPlanStepIds(null);
        return;
      }
      if (validIds.length !== focusedPlanStepIds.length) {
        setFocusedPlanStepIds(validIds);
      }
    }
  }, [focusedPlanStepId, focusedPlanStepIds, planStepSnapshotById]);
  const planStepPrimitiveById = useMemo(() => {
    const map = new Map();
    definedSteps.forEach((primitive) => {
      const refId = primitive?.referenceParameters?.stepId ?? primitive?.id;
      if (refId) {
        map.set(refId, primitive);
      }
    });
    return map;
  }, [definedSteps]);
  const planPrimitiveIdToStepId = useMemo(() => {
    const map = new Map();
    planStepPrimitiveById.forEach((primitive, stepId) => {
      const primitiveId = getPrimitiveId(primitive);
      if (primitiveId) {
        map.set(primitiveId, stepId);
      }
    });
    return map;
  }, [planStepPrimitiveById]);
  const planDescendants = useMemo(
    () => (planPrimitive?.primitives?.descendants ?? EMPTY_ARRAY),
    [planPrimitive, planStatsVersion],
  );
  const descendantLookup = useMemo(() => {
    const map = new Map();
    planDescendants.forEach((primitiveItem) => {
      const primitiveId = getPrimitiveId(primitiveItem);
      if (!primitiveId) {
        return;
      }
      map.set(primitiveId, primitiveItem);
    });
    return map;
  }, [planDescendants]);
  const getPrimitiveById = useCallback(
    (primitiveId) => {
      if (!primitiveId) {
        return null;
      }
      return descendantLookup.get(primitiveId) ?? mainstore.primitive(primitiveId) ?? null;
    },
    [descendantLookup, mainstore],
  );
  const planStepParentLookup = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step) => {
      const stepId = step?.id;
      const parentId = getPlanStepParentId(step);
      if (!stepId || !parentId) {
        return;
      }
      map.set(stepId, parentId);
    });
    return map;
  }, [planStepSnapshots]);
  const planStepChildrenMap = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step) => {
      const stepId = step?.id;
      const parentId = planStepParentLookup.get(stepId);
      if (!stepId || !parentId) {
        return;
      }
      if (!map.has(parentId)) {
        map.set(parentId, []);
      }
      map.get(parentId).push(stepId);
    });
    return map;
  }, [planStepSnapshots, planStepParentLookup]);
  const planStepInputsMap = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step) => {
      const stepId = step?.id;
      if (!stepId || !Array.isArray(step?.inputs) || step.inputs.length === 0) {
        return;
      }
      const inputIds = step.inputs
        .map((input) => (typeof input?.stepId === "string" ? input.stepId : null))
        .filter(Boolean);
      if (inputIds.length) {
        map.set(stepId, inputIds);
      }
    });
    return map;
  }, [planStepSnapshots]);
  const planStepHasConsumerSet = useMemo(() => {
    const set = new Set();
    planStepInputsMap.forEach((inputIds) => {
      inputIds.forEach((inputId) => {
        if (inputId) {
          set.add(inputId);
        }
      });
    });
    return set;
  }, [planStepInputsMap]);
  const planStepPipelineKeyById = useMemo(() => {
    const map = new Map();
    planStepSnapshots.forEach((step) => {
      const stepId = step?.id;
      if (!stepId) {
        return;
      }
      const pipelineKey =
        step?.metadata?.pipeline?.key ??
        step?.executionState?.pipeline?.key ??
        null;
      if (pipelineKey) {
        map.set(stepId, pipelineKey);
      }
    });
    return map;
  }, [planStepSnapshots]);
  const planStepConsumedPipelineKeySet = useMemo(() => {
    const set = new Set();
    planStepSnapshots.forEach((step) => {
      const upstreamKeys =
        step?.metadata?.pipeline?.upstreamKeys ??
        step?.executionState?.pipeline?.upstreamKeys ??
        null;
      if (!Array.isArray(upstreamKeys) || upstreamKeys.length === 0) {
        return;
      }
      upstreamKeys.forEach((key) => {
        if (key) {
          set.add(key);
        }
      });
    });
    return set;
  }, [planStepSnapshots]);
  const analysisLeafStepIdSet = useMemo(() => {
    if (!planStepSnapshots.length) {
      return null;
    }
    const isAuxiliaryStep = (step) => {
      if (!step) return false;
      const metadata = step.metadata ?? {};
      const isGapfillId = typeof step.id === "string" && step.id.startsWith("gapfill_");
      const isFilterCompanySet =
        metadata.templateId === "filter_company_set" ||
        step.templateId === "filter_company_set";
      const isGapfillKind = metadata.kind === "gap_fill";
      return isGapfillId || isFilterCompanySet || isGapfillKind;
    };
    const set = new Set();
    planStepSnapshots.forEach((step) => {
      const stepId = step?.id;
      if (!stepId) {
        return;
      }
      const isSubstantiveAnalysis =
        step?.area === "analysis" &&
        Boolean(step?.metadata?.optionId) &&
        !isAuxiliaryStep(step);
      if (isSubstantiveAnalysis) {
        set.add(stepId);
        return;
      }
      const children = planStepChildrenMap.get(stepId) ?? [];
      const hasChildren = children.length > 0;
      const hasConsumer = planStepHasConsumerSet.has(stepId);
      const pipelineKey = planStepPipelineKeyById.get(stepId);
      const pipelineConsumed =
        pipelineKey && planStepConsumedPipelineKeySet.has(pipelineKey);
      if (!hasChildren && !hasConsumer && !pipelineConsumed) {
        set.add(stepId);
      }
    });
    return set;
  }, [
    planStepChildrenMap,
    planStepConsumedPipelineKeySet,
    planStepHasConsumerSet,
    planStepPipelineKeyById,
    planStepSnapshots,
  ]);
  const focusedPlanStepScopeSet = useMemo(() => {
    const seedIds = focusedPlanStepIds?.length
      ? focusedPlanStepIds
      : focusedPlanStepId
      ? [focusedPlanStepId]
      : [];
    if (!seedIds.length) {
      return null;
    }
    const allowed = new Set(seedIds);
    const queue = [...seedIds];
    while (queue.length) {
      const current = queue.shift();
      const children = planStepChildrenMap.get(current);
      if (!children) {
        continue;
      }
      children.forEach((childId) => {
        if (!childId || allowed.has(childId)) {
          return;
        }
        allowed.add(childId);
        queue.push(childId);
      });
    }
    seedIds.forEach((seedId) => {
      let ancestorCursor = seedId;
      while (planStepParentLookup.has(ancestorCursor)) {
        const parentId = planStepParentLookup.get(ancestorCursor);
        if (!parentId) {
          break;
        }
        if (!allowed.has(parentId)) {
          allowed.add(parentId);
        }
        ancestorCursor = parentId;
      }
    });
    return allowed;
  }, [focusedPlanStepId, focusedPlanStepIds, planStepChildrenMap, planStepParentLookup]);
  const workstreamStepScopeSet = useMemo(() => {
    if (!selectedWorkstreamId) {
      return null;
    }
    const workstream = workstreamById.get(selectedWorkstreamId);
    if (!workstream) {
      return null;
    }
    const stepIds = new Set();
    const linkedSteps = workstream?.primitives?.goal?.allStep ?? [];
    linkedSteps.forEach((stepPrimitive) => {
      const primitiveId = getPrimitiveId(stepPrimitive);
      if (!primitiveId) {
        return;
      }
      const stepId = planPrimitiveIdToStepId.get(primitiveId);
      if (stepId) {
        stepIds.add(stepId);
      }
    });
    return stepIds.size ? stepIds : null;
  }, [planPrimitiveIdToStepId, selectedWorkstreamId, workstreamById]);
  const expandedWorkstreamStepScopeSet = useMemo(() => {
    if (!workstreamStepScopeSet || workstreamStepScopeSet.size === 0) {
      return null;
    }
    const expanded = new Set(workstreamStepScopeSet);
    const queue = Array.from(workstreamStepScopeSet);
    const isAuxStep = (stepId) => {
      if (!stepId) return false;
      const step = planStepSnapshotById.get(stepId);
      if (!step) return false;
      const origin = step.metadata?.origin ?? step.metadata?.kind ?? null;
      if (origin === "advisor" || origin === "gap_fill") return true;
      if (typeof step.id === "string" && step.id.startsWith("gapfill_")) return true;
      return false;
    };
    while (queue.length) {
      const current = queue.shift();
      if (!current) continue;
      const neighbors = new Set();
      const parentId = planStepParentLookup.get(current);
      if (parentId) neighbors.add(parentId);
      (planStepChildrenMap.get(current) ?? []).forEach((childId) => neighbors.add(childId));
      (planStepInputsMap.get(current) ?? []).forEach((inputId) => neighbors.add(inputId));
      planStepInputsMap.forEach((inputIds, stepId) => {
        if (inputIds?.includes?.(current)) {
          neighbors.add(stepId);
        }
      });
      neighbors.forEach((neighborId) => {
        if (!neighborId || expanded.has(neighborId)) return;
        if (!isAuxStep(neighborId)) return;
        expanded.add(neighborId);
        queue.push(neighborId);
      });
    }
    return expanded;
  }, [
    planStepChildrenMap,
    planStepInputsMap,
    planStepParentLookup,
    planStepSnapshotById,
    workstreamStepScopeSet,
  ]);
  const workstreamPlanSnapshotForScope = useMemo(() => {
    if (!planSnapshotForScope || !selectedWorkstreamId || !expandedWorkstreamStepScopeSet || expandedWorkstreamStepScopeSet.size === 0) {
      return planSnapshotForScope;
    }
    const steps = (planSnapshotForScope.steps ?? []).filter((step) => {
      const stepId = step?.id ?? step?.stepId ?? null;
      return stepId && expandedWorkstreamStepScopeSet.has(stepId);
    });
    const activeStepId =
      planSnapshotForScope.activeStepId && expandedWorkstreamStepScopeSet.has(planSnapshotForScope.activeStepId)
        ? planSnapshotForScope.activeStepId
        : null;
    return {
      ...planSnapshotForScope,
      steps,
      activeStepId,
    };
  }, [expandedWorkstreamStepScopeSet, planSnapshotForScope, selectedWorkstreamId]);
  const visibleOutputSteps = useMemo(() => {
    let next = outputSteps;
    if (expandedWorkstreamStepScopeSet && expandedWorkstreamStepScopeSet.size > 0) {
      next = next.filter((entry) => expandedWorkstreamStepScopeSet.has(entry.stepId));
    }
    if (analysisLeafOnly && analysisLeafStepIdSet) {
      next = next.filter((entry) => entry.area !== "analysis" || analysisLeafStepIdSet.has(entry.stepId));
    }
    if (!focusedPlanStepId || !focusedPlanStepScopeSet || focusedPlanStepScopeSet.size === 0) {
      return next;
    }
    return next.filter((entry) => focusedPlanStepScopeSet.has(entry.stepId));
  }, [
    analysisLeafOnly,
    analysisLeafStepIdSet,
    expandedWorkstreamStepScopeSet,
    focusedPlanStepId,
    focusedPlanStepScopeSet,
    outputSteps,
  ]);
  const anchorPrimitiveCandidates = useMemo(() => {
    const reference = minimalPlanReference ?? {};
    const anchor = reference.anchor ?? {};
    const candidates = [
      anchor.primaryPrimitiveId,
      reference.activeAnchorId,
      anchor.segmentId,
      anchor.companyId,
    ].filter(Boolean);
    return Array.from(new Set(candidates));
  }, [minimalPlanReference]);
  const focusedPrimitiveSelectionSet = useMemo(() => {
    if (!focusedPlanStepId) {
      return null;
    }
    const collected = new Set();
    const visited = new Set();
    const queue = [focusedPlanStepId];
    while (queue.length) {
      const currentStepId = queue.shift();
      if (!currentStepId || visited.has(currentStepId)) {
        continue;
      }
      visited.add(currentStepId);
      const stepSnapshot = planStepSnapshotById.get(currentStepId);
      const stepPrimitive = planStepPrimitiveById.get(currentStepId);
      const beforeCount = collected.size;
      if (stepSnapshot && (stepSnapshot.area === "analysis" || stepSnapshot.area === "presentation")) {
        const outputs = outputStepsById.get(currentStepId) ?? EMPTY_ARRAY;
        outputs.forEach((primitive) => addPrimitiveAndItems(collected, primitive));
      }
      if (stepPrimitive?.primitives?.origin) {
        const viewChildren = stepPrimitive.primitives.origin.allView ?? [];
        viewChildren.forEach((view) => addPrimitiveAndItems(collected, view));
        const entityChildren = stepPrimitive.primitives.origin.allEntity ?? [];
        entityChildren.forEach((entity) => {
          if (entity?.referenceParameters?.role === "anchor") {
            addPrimitiveAndItems(collected, entity);
          }
        });
      }
      const execIds = extractPrimitiveIdsFromExecutionState(stepSnapshot?.executionState);
      execIds.forEach((primitiveId) => addPrimitiveAndItems(collected, getPrimitiveById(primitiveId)));
      if (stepSnapshot?.id && /anchor/i.test(stepSnapshot.id)) {
        anchorPrimitiveCandidates.forEach((anchorPrimitiveId) => {
          addPrimitiveAndItems(collected, getPrimitiveById(anchorPrimitiveId));
        });
      }
      if (collected.size === beforeCount) {
        const inputStepIds = planStepInputsMap.get(currentStepId) ?? EMPTY_ARRAY;
        inputStepIds.forEach((inputStepId) => {
          if (inputStepId && !visited.has(inputStepId)) {
            queue.push(inputStepId);
          }
        });
      }
      const parentId = planStepParentLookup.get(currentStepId);
      if (parentId) {
        queue.push(parentId);
      }
    }
    return collected.size ? collected : null;
  }, [
    anchorPrimitiveCandidates,
    focusedPlanStepId,
    getPrimitiveById,
    outputStepsById,
    planStepParentLookup,
    planStepInputsMap,
    planStepPrimitiveById,
    planStepSnapshotById,
  ]);
  const doesItemMatchFocusedStep = useCallback(
    (item) => {
      if (!focusedPlanStepId) {
        return true;
      }
      const primitiveId = getPrimitiveId(item);
      if (primitiveId && focusedPrimitiveSelectionSet?.has(primitiveId)) {
        return true;
      }
      if (!focusedPlanStepScopeSet || !focusedPlanStepScopeSet.size) {
        return false;
      }
      let ownerStepId = getPrimitiveOwnerStepId(item);
      if (!ownerStepId) {
        ownerStepId = resolveOwnerStepFromParents(item, planPrimitiveIdToStepId, getPrimitiveById);
      }
      return ownerStepId ? focusedPlanStepScopeSet.has(ownerStepId) : false;
    },
    [
      focusedPlanStepId,
      focusedPrimitiveSelectionSet,
      focusedPlanStepScopeSet,
      getPrimitiveById,
      planPrimitiveIdToStepId,
    ],
  );
  const filterGroupsByFocusedPlanStep = useCallback(
    (groups) => {
      if (!focusedPlanStepId) {
        return groups;
      }
      return groups
        .map((group) => {
          const filteredItems = group.items?.filter((item) => doesItemMatchFocusedStep(item));
          if (!filteredItems || filteredItems.length === 0) {
            return null;
          }
          return {
            ...group,
            items: filteredItems,
            count: filteredItems.length,
          };
        })
        .filter(Boolean);
    },
    [focusedPlanStepId, doesItemMatchFocusedStep],
  );
  const getPlanStepLabel = useCallback(
    (step, fallbackIdx = 0) => {
      if (!step) {
        return `Task ${fallbackIdx + 1}`;
      }
      if (step.name) {
        return step.name;
      }
      if (step.title) {
        return step.title;
      }
      if (step.description) {
        return step.description;
      }
      const order =
        typeof step.order === "number"
          ? step.order
          : step.id && planStepIndexLookup.has(step.id)
            ? planStepIndexLookup.get(step.id)
            : null;
      if (Number.isFinite(order)) {
        return `Task ${order + 1}`;
      }
      return `Task ${fallbackIdx + 1}`;
    },
    [planStepIndexLookup],
  );
  const focusedPlanStep = focusedPlanStepId ? planStepSnapshotById.get(focusedPlanStepId) ?? null : null;
  const getOwnerStepId = useCallback(
    (item) => {
      if (!item) return null;
      const primitiveId = getPrimitiveId(item);
      if (!primitiveId) return null;
      const direct = planPrimitiveIdToStepId.get(primitiveId) ?? null;
      if (direct) return direct;
      return resolveOwnerStepFromParents(item, planPrimitiveIdToStepId, getPrimitiveById);
    },
    [getPrimitiveById, planPrimitiveIdToStepId],
  );
  const discoveryContent = useMemo(() => {
    if (!discoveryActiveOnly) {
      return discoveryGroups;
    }
    return discoveryGroups
      .map((group) => {
        const activeItems = group.items?.filter?.(isPrimitiveActive) ?? [];
        if (!activeItems.length) {
          return null;
        }
        return {
          ...group,
          count: activeItems.length,
          items: activeItems,
        };
      })
      .filter(Boolean);
  }, [discoveryActiveOnly, discoveryGroups]);

  const evidenceContent = evidenceGroups;
  const workstreamDiscoveryContent = useMemo(() => {
    if (!selectedWorkstreamId || !expandedWorkstreamStepScopeSet || expandedWorkstreamStepScopeSet.size === 0) {
      return discoveryContent;
    }
    return discoveryContent
      .map((group) => {
        const items = (group.items ?? []).filter((item) => {
          const ownerStepId = getOwnerStepId(item);
          return ownerStepId && expandedWorkstreamStepScopeSet.has(ownerStepId);
        });
        if (!items.length) {
          return null;
        }
        return {
          ...group,
          items,
          count: items.length,
        };
      })
      .filter(Boolean);
  }, [discoveryContent, expandedWorkstreamStepScopeSet, getOwnerStepId, selectedWorkstreamId]);
  const workstreamEvidenceContent = useMemo(() => {
    if (!selectedWorkstreamId || !expandedWorkstreamStepScopeSet || expandedWorkstreamStepScopeSet.size === 0) {
      return evidenceContent;
    }
    return evidenceContent
      .map((group) => {
        const items = (group.items ?? []).filter((item) => {
          const ownerStepId = getOwnerStepId(item);
          return ownerStepId && expandedWorkstreamStepScopeSet.has(ownerStepId);
        });
        if (!items.length) {
          return null;
        }
        return {
          ...group,
          items,
          count: items.length,
        };
      })
      .filter(Boolean);
  }, [evidenceContent, expandedWorkstreamStepScopeSet, getOwnerStepId, selectedWorkstreamId]);

  const columnCount = useMemo(
    () => getColumnCountForWidth(rightPaneWidth || viewportWidth),
    [rightPaneWidth, viewportWidth],
  );
  const focusedDiscoveryContent = useMemo(
    () => filterGroupsByFocusedPlanStep(workstreamDiscoveryContent),
    [workstreamDiscoveryContent, filterGroupsByFocusedPlanStep],
  );
  const focusedEvidenceContent = useMemo(
    () => filterGroupsByFocusedPlanStep(workstreamEvidenceContent),
    [workstreamEvidenceContent, filterGroupsByFocusedPlanStep],
  );
  const gridColumnCount = useMemo(() => {
    if (!Number.isFinite(rightPaneWidth) || rightPaneWidth <= 0) {
      return columnCount;
    }
    if (rightPaneWidth < 900) {
      return 1;
    }
    return columnCount;
  }, [columnCount, rightPaneWidth]);
  const discoveryRows = useMemo(
    () => chunkIntoRows(focusedDiscoveryContent, gridColumnCount),
    [focusedDiscoveryContent, gridColumnCount],
  );
  const evidenceRows = useMemo(
    () => chunkIntoRows(focusedEvidenceContent, gridColumnCount),
    [focusedEvidenceContent, gridColumnCount],
  );
  const selectedDiscoveryRowIndex = useMemo(() => {
    if (!selectedDataGroup || selectedDataGroup.kind !== "discovery") {
      return -1;
    }
    const idx = focusedDiscoveryContent.findIndex((group) => group.title === selectedDataGroup.title);
    if (idx === -1) {
      return -1;
    }
    return Math.floor(idx / gridColumnCount);
  }, [selectedDataGroup, focusedDiscoveryContent, gridColumnCount]);
  const selectedDiscoveryColumnIndex = useMemo(() => {
    if (!selectedDataGroup || selectedDataGroup.kind !== "discovery") {
      return -1;
    }
    const idx = focusedDiscoveryContent.findIndex((group) => group.title === selectedDataGroup.title);
    if (idx === -1) {
      return -1;
    }
    return idx % gridColumnCount;
  }, [selectedDataGroup, focusedDiscoveryContent, gridColumnCount]);
  const selectedEvidenceRowIndex = useMemo(() => {
    if (!selectedDataGroup || selectedDataGroup.kind !== "evidence") {
      return -1;
    }
    const idx = focusedEvidenceContent.findIndex((group) => group.title === selectedDataGroup.title);
    if (idx === -1) {
      return -1;
    }
    return Math.floor(idx / gridColumnCount);
  }, [selectedDataGroup, focusedEvidenceContent, gridColumnCount]);
  const selectedEvidenceColumnIndex = useMemo(() => {
    if (!selectedDataGroup || selectedDataGroup.kind !== "evidence") {
      return -1;
    }
    const idx = focusedEvidenceContent.findIndex((group) => group.title === selectedDataGroup.title);
    if (idx === -1) {
      return -1;
    }
    return idx % gridColumnCount;
  }, [selectedDataGroup, focusedEvidenceContent, gridColumnCount]);

  const discoverySummary = useMemo(() => {
    let sources = 0;
    let evidenceTotal = 0;
    let running = false;
    for (const group of focusedDiscoveryContent) {
      sources += group.count ?? group.items?.length ?? 0;
      if (!running && group.items?.some?.(isPrimitiveActive)) {
        running = true;
      }
    }
    for (const group of focusedEvidenceContent) {
      evidenceTotal += group.count ?? group.items?.length ?? 0;
    }
    return {
      sources,
      evidenceTotal,
      running,
    };
  }, [focusedDiscoveryContent, focusedEvidenceContent]);

  const resolvedSelectedDataGroup = useMemo(() => {
    if (!selectedDataGroup) {
      return null;
    }
    const sourceList = selectedDataGroup.kind === "evidence" ? focusedEvidenceContent : focusedDiscoveryContent;
    const match = sourceList.find((group) => group.title === selectedDataGroup.title);
    if (!match) {
      return null;
    }
    return {
      ...match,
      kind: selectedDataGroup.kind,
    };
  }, [selectedDataGroup, focusedDiscoveryContent, focusedEvidenceContent]);
  useEffect(() => {
    if (selectedDataGroup && !resolvedSelectedDataGroup) {
      setSelectedDataGroup(null);
    }
  }, [selectedDataGroup, resolvedSelectedDataGroup]);

  const handleDataGroupSelect = useCallback(
    (kind, title) => {
      const sourceList = kind === "evidence" ? focusedEvidenceContent : focusedDiscoveryContent;
      const target = sourceList.find((group) => group.title === title);
      if (!target) {
        return;
      }
      setSelectedDataGroup({
        kind,
        title: target.title,
      });
    },
    [focusedDiscoveryContent, focusedEvidenceContent],
  );

  const handleCloseSelectedDataGroup = useCallback(() => {
    setSelectedDataGroup(null);
  }, []);

  const renderSelectedDataGroupDrawer = (key, { disableLeftRadius = false, disableRightRadius = false } = {}) => {
    if (
      !resolvedSelectedDataGroup ||
      (resolvedSelectedDataGroup.kind !== "discovery" && resolvedSelectedDataGroup.kind !== "evidence")
    ) {
      return null;
    }
    const { kind, items = EMPTY_ARRAY, count } = resolvedSelectedDataGroup;
    const itemCount = count ?? items.length ?? 0;
    const hasItems = Array.isArray(items) && items.length > 0;

    const drawerBgClass =
      kind === "discovery"
        ? "via-20% to-50% border-blue-200 bg-gradient-to-b from-blue-50 via-blue-50 to-white dark:border-blue-500/40 dark:bg-gradient-to-b dark:from-blue-900/40 dark:via-blue-900/70 dark:to-slate-900"
        : "via-20% to-50% border-blue-200 bg-gradient-to-b from-blue-50 via-blue-50 to-white dark:border-blue-500/40 dark:bg-gradient-to-b dark:from-blue-900/40 dark:via-blue-900/70 dark:to-slate-900";

    return (
      <div
        key={key}
        className={`relative col-span-full -mt-px mb-8 rounded-2xl border border-t-0 pt-1 px-3 pb-3 ${drawerBgClass}`}
        style={{
          borderTopLeftRadius: disableLeftRadius ? 0 : "1rem",
          borderTopRightRadius: disableRightRadius ? 0 : "1rem",
        }}
      >
        {kind === "discovery" ? (
          hasItems ? (
            <div className="mt-3 grid gap-2 md:grid-cols-2 2xl:grid-cols-3">
              {items.map((item) => {
                const updatedAt = formatUpdatedLabel(
                  item.referenceParameters?.updated_at ??
                    item.referenceParameters?.updatedAt ??
                    item.updated_at ??
                    item.updatedAt,
                );
                return (
                  <button
                    type="button"
                    key={item.id ?? item._id ?? item.plainId}
                    onClick={() => mainstore.sidebarSelect?.(item)}
                    className="rounded-lg border border-slate-200 bg-white p-2 text-left text-xs transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/70 dark:hover:border-white/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[13px] font-semibold text-slate-900 dark:text-white">
                          {formatPrimitiveDisplayTitle(item)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          {item.displayType || item.metadata?.title || item.type}
                        </p>
                      </div>
                      <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:border-white/10 dark:text-white/80">
                        {formatDiscoveryStatus(item)}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Updated {updatedAt}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-300">No records found for this selection.</p>
          )
        ) : hasItems ? (
          <div className="mt-3">
            <Table
              primitive={items[0] ?? null}
              data={items}
              enableCopy={false}
              pageItems={10}
              className="w-full !text-xs"
              compact={true}
              onExpand={(item) => mainstore.sidebarSelect(item)}
            />
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-300">No records found for this selection.</p>
        )}
      </div>
    );
  };

  useEffect(() => {
    const activeKey = `section_${activeTaskArea}`;
    setExpandedSections((prev) => {
      if (prev.has(activeKey)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(activeKey);
      return next;
    });
  }, [activeTaskArea]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectedStepSnapshot = selectedOutputView
    ? planStepSnapshotById.get(selectedOutputView.entry.stepId) ?? null
    : null;
  const selectedStepPrimitive = selectedOutputView
    ? planStepPrimitiveById.get(selectedOutputView.entry.stepId) ?? null
    : null;
  const fallbackSubChat = useMemo(() => {
    if (!selectedOutputView) {
      return null;
    }
    const title = selectedOutputView.entry?.name ?? selectedStepSnapshot?.name ?? "Focused output chat";
    const description = selectedOutputView.entry?.description ?? selectedStepSnapshot?.description ?? null;
    const optionId =
      selectedStepSnapshot?.metadata?.optionId ??
      selectedStepPrimitive?.metadata?.optionId ??
      selectedOutputView.entry?.metadata?.optionId ??
      selectedOutputView.entry?.optionId ??
      null;
    return {
      title,
      description,
      optionId,
      permittedChanges: [],
      redirectedChanges: [],
    };
  }, [selectedOutputView, selectedStepSnapshot, selectedStepPrimitive]);
  const baseFocusedScope = useMemo(() => {
    if (!selectedOutputView || !selectedStepPrimitive || !planPrimitiveId) {
      return null;
    }
    const planReference = scopedPlanReference ? { ...scopedPlanReference } : null;
    const fallbackReference = minimalPlanReference ? { ...minimalPlanReference } : null;
    const focusValue =
      planReference?.focus ??
      planReference?.focusId ??
      planReference?.focus_dimension ??
      planReference?.focusDimension ??
      fallbackReference?.focus ??
      fallbackReference?.focus_dimension ??
      fallbackReference?.focusDimension ??
      null;
    return {
      constrainTo: selectedStepPrimitive.id,
      planContext: {
        focus: focusValue,
        area: selectedStepSnapshot?.area ?? planReference?.area ?? fallbackReference?.area ?? activeTaskArea,
        planPrimitiveId,
        planReference: planReference ?? fallbackReference,
        plan: planSnapshotForScope,
        workspaceId: primitive.workspaceId ?? null,
      },
    };
  }, [
    selectedOutputView,
    selectedStepPrimitive,
    selectedStepSnapshot,
    planPrimitiveId,
    scopedPlanReference,
    minimalPlanReference,
    planSnapshotForScope,
    activeTaskArea,
    workspaceId,
  ]);

  const effectiveSubChatScope = useMemo(() => {
    if (!baseFocusedScope) {
      return null;
    }
    const subChatConfig =
      selectedStepSnapshot?.metadata?.subChat ??
      selectedStepPrimitive?.referenceParameters?.metadata?.subChat ??
      selectedStepPrimitive?.metadata?.subChat ??
      selectedOutputView?.entry?.metadata?.subChat ??
      selectedOutputView?.entry?.subChat ??
      fallbackSubChat ??
      null;
    if (!subChatConfig) {
      return baseFocusedScope;
    }
    const optionId =
      subChatConfig.optionId ??
      selectedStepSnapshot?.metadata?.optionId ??
      selectedStepPrimitive?.metadata?.optionId ??
      selectedOutputView?.entry?.metadata?.optionId ??
      selectedOutputView?.entry?.optionId ??
      null;
    return {
      ...baseFocusedScope,
      subChat: {
        ...subChatConfig,
        optionId,
        stepId: selectedStepSnapshot?.id ?? null,
        flowPrimitiveId:
          selectedOutputView?.primitive?.id ??
          selectedStepSnapshot?.executionState?.primaryPrimitiveId ??
          null,
      },
    };
  }, [baseFocusedScope, fallbackSubChat, selectedStepSnapshot, selectedStepPrimitive, selectedOutputView]);
  const workstreamSubChatScope = useMemo(() => {
    if (!workstreamChatPrimitive || !selectedWorkstreamId || !planPrimitiveId) {
      return null;
    }
    const workstream = workstreamById.get(selectedWorkstreamId);
    const workstreamGoalId =
      workstream?.referenceParameters?.goalId ??
      workstream?.referenceParameters?.metadata?.goalId ??
      null;
    const planReference = scopedPlanReference ? { ...scopedPlanReference } : minimalPlanReference ? { ...minimalPlanReference } : null;
    const focusValue =
      planReference?.focus ??
      planReference?.focusId ??
      planReference?.focus_dimension ??
      planReference?.focusDimension ??
      null;
    return {
      constrainTo: workstreamChatPrimitive.id,
      planContext: {
        focus: focusValue,
        area: planReference?.area ?? activeTaskArea,
        planPrimitiveId,
        planReference,
        plan: workstreamPlanSnapshotForScope,
        workspaceId: primitive.workspaceId ?? null,
        workstreamId: selectedWorkstreamId,
        goalId: workstreamGoalId ?? null,
      },
    };
  }, [
    activeTaskArea,
    minimalPlanReference,
    planPrimitiveId,
    workstreamPlanSnapshotForScope,
    primitive.workspaceId,
    scopedPlanReference,
    selectedWorkstreamId,
    workstreamById,
    workstreamChatPrimitive,
  ]);
  useEffect(() => {
    setOutputCarouselIndexMap((prev) => {
      const prevKeys = Object.keys(prev || {});
      if (!visibleOutputSteps.length) {
        return prevKeys.length ? {} : prev;
      }
      const next = {};
      let changed = prevKeys.length !== visibleOutputSteps.length;
      visibleOutputSteps.forEach((entry) => {
        const current = prev[entry.stepId] ?? 0;
        const nextValue = entry.outputs.length ? Math.min(current, entry.outputs.length - 1) : 0;
        next[entry.stepId] = nextValue;
        if (prev[entry.stepId] !== nextValue) {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [visibleOutputSteps]);

  useEffect(() => {
    if (!selectedOutputView) {
      return;
    }
    const entryStillExists = visibleOutputSteps.some((entry) => entry.stepId === selectedOutputView.entry.stepId);
    if (!entryStillExists) {
      setSelectedOutputView(null);
    }
  }, [visibleOutputSteps, selectedOutputView]);
  useEffect(() => {
    if (subChatOwner === "output" && !selectedOutputView && subChatSessionInfo?.id) {
      summarizeSubChatSession(subChatSessionInfo);
      setSubChatSessionInfo(null);
      setSubChatOwner(null);
    }
  }, [selectedOutputView, subChatOwner, subChatSessionInfo, summarizeSubChatSession]);
  useEffect(() => {
    if (subChatOwner === "workstream" && !selectedWorkstreamId && subChatSessionInfo?.id) {
      summarizeSubChatSession(subChatSessionInfo);
      setSubChatSessionInfo(null);
      setSubChatOwner(null);
    }
  }, [selectedWorkstreamId, subChatOwner, subChatSessionInfo, summarizeSubChatSession]);

  useEffect(() => {
    if (!rightPaneNode) {
      return undefined;
    }
    let frameId;
    const updateWidth = () => {
      frameId = requestAnimationFrame(() => {
        const width = rightPaneNode.getBoundingClientRect().width;
        if (Number.isFinite(width) && width > 0) {
          setRightPaneWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
        }
      });
    };
    updateWidth();
    if (typeof window === "undefined") {
      return undefined;
    }
    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const width = entry.contentRect?.width;
          if (Number.isFinite(width) && width > 0) {
            setRightPaneWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
          }
        });
      });
      observer.observe(rightPaneNode);
      return () => {
        if (frameId) {
          cancelAnimationFrame(frameId);
        }
        observer.disconnect();
      };
    }
    window.addEventListener("resize", updateWidth);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener("resize", updateWidth);
    };
  }, [rightPaneNode]);
  const outputStepsByArea = useMemo(() => {
    if (!visibleOutputSteps.length) {
      return new Map();
    }
    const map = new Map();
    visibleOutputSteps.forEach((entry) => {
      const key = entry.area || "analysis";
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(entry);
    });
    return map;
  }, [visibleOutputSteps]);
  const artefactsCanvasEntries = useMemo(
    () => {
      const collectDiscoveryRenderables = (step, stepPrimitive, existingOutputs) => {
        const candidates = [];
        const pushCandidate = (primitive) => {
          if (
            primitive &&
            typeof primitive === "object" &&
            primitive.id &&
            typeof primitive.type === "string" &&
            DISCOVERY_CANVAS_RENDER_TYPES.has(primitive.type)
          ) {
            candidates.push(primitive);
          }
        };
        (existingOutputs ?? EMPTY_ARRAY).forEach(pushCandidate);
        (stepPrimitive?.primitives?.descendants ?? EMPTY_ARRAY).forEach(pushCandidate);
        const executionIds = extractPrimitiveIdsFromExecutionState(step?.executionState);
        executionIds.forEach((primitiveId) => pushCandidate(getPrimitiveById(primitiveId)));
        return uniquePrimitives(candidates);
      };

      const byStepId = new Map();
      const baseEntries = [
        ...(outputStepsByArea.get("analysis") ?? EMPTY_ARRAY),
        ...(outputStepsByArea.get("presentation") ?? EMPTY_ARRAY),
      ];
      baseEntries.forEach((entry) => {
        if (entry?.stepId) {
          byStepId.set(entry.stepId, entry);
        }
      });

      planStepSnapshots.forEach((step, idx) => {
        const stepId = step?.id;
        if (!stepId || byStepId.has(stepId)) {
          return;
        }
        if (expandedWorkstreamStepScopeSet?.size > 0 && !expandedWorkstreamStepScopeSet.has(stepId)) {
          return;
        }
        if (focusedPlanStepScopeSet?.size > 0 && !focusedPlanStepScopeSet.has(stepId)) {
          return;
        }
        const stepPrimitive = planStepPrimitiveById.get(stepId);
        const primitiveType = stepPrimitive?.type;
        const isDiscoveryStep = step?.area === "discovery" || DISCOVERY_SOURCE_TYPES.has(primitiveType);
        if (!isDiscoveryStep) {
          return;
        }
        const outputs = outputStepsById.get(stepId) ?? EMPTY_ARRAY;
        const discoveryRenderables = collectDiscoveryRenderables(step, stepPrimitive, outputs);
        const preferredOutputs = discoveryRenderables.length ? discoveryRenderables : outputs;
        if (!preferredOutputs.length) {
          return;
        }
        byStepId.set(stepId, {
          stepId,
          area: "discovery",
          order: Number.isFinite(step?.order)
            ? step.order
            : Number.isFinite(idx)
              ? idx
              : Number.MAX_SAFE_INTEGER,
          name: step?.name ?? step?.title ?? stepPrimitive?.title ?? "Evidence search",
          description: step?.description ?? "",
          outputs: preferredOutputs,
        });
      });

      if (INCLUDE_DIRECT_QUERY_DIAGNOSTIC_SUMMARIES) {
        const normalizeToIdList = (value) => {
          if (!value) {
            return [];
          }
          const list = Array.isArray(value) ? value : [value];
          return list
            .flatMap((entry) =>
              typeof entry === "object" && entry
                ? [
                    entry.id,
                    entry.primitiveId,
                    entry.refId,
                    entry.inputId,
                    entry.inputRef,
                    entry.sourceInputId,
                    entry.sourcePrimitiveId,
                    entry.sourceId,
                  ]
                : [entry],
            )
            .filter((entry) => typeof entry === "string" && entry.trim().length > 0);
        };
        const collectIdsByKeyMatcher = (root, keyMatcher) => {
          const found = new Set();
          const visited = new Set();
          const stack = [root];
          while (stack.length) {
            const current = stack.pop();
            if (!current || typeof current !== "object") {
              continue;
            }
            if (visited.has(current)) {
              continue;
            }
            visited.add(current);
            if (Array.isArray(current)) {
              current.forEach((item) => stack.push(item));
              continue;
            }
            Object.entries(current).forEach(([key, value]) => {
              const keyLower = String(key).toLowerCase();
              if (keyMatcher(keyLower)) {
                normalizeToIdList(value).forEach((id) => found.add(id));
              }
              if (value && typeof value === "object") {
                stack.push(value);
              }
            });
          }
          return Array.from(found);
        };
        const collectSynthesisInputRefs = (root) =>
          collectIdsByKeyMatcher(root, (keyLower) =>
            keyLower === "input_refs" ||
            keyLower === "input_ref" ||
            keyLower === "inputrefs" ||
            keyLower === "inputref" ||
            keyLower === "source_input_ids" ||
            keyLower === "source_input_id" ||
            keyLower === "source_input_refs" ||
            keyLower === "source_input_ref",
          );
        const collectSynthesisPrimitiveRefs = (root) =>
          collectIdsByKeyMatcher(root, (keyLower) =>
            keyLower === "primitiveid" ||
            keyLower === "primitiveids" ||
            keyLower === "primitive_id" ||
            keyLower === "primitive_ids" ||
            keyLower === "sourceprimitiveid" ||
            keyLower === "sourceprimitiveids" ||
            keyLower === "source_primitive_id" ||
            keyLower === "source_primitive_ids",
          );

        const directPlanQueries = (planPrimitive?.primitives?.origin?.allItems ?? EMPTY_ARRAY).filter(
          (item) =>
            item?.type === "query" &&
            item?.id &&
            Array.isArray(item?.parentPrimitiveIds) &&
            item.parentPrimitiveIds.includes(planPrimitive?.id) &&
            !planPrimitiveIdToStepId.has(item.id),
        );

        const stepIdByPrimitiveId = new Map();
        const existingCanvasPrimitiveIds = new Set();
        byStepId.forEach((entry, stepId) => {
          (entry?.outputs ?? EMPTY_ARRAY).forEach((primitiveItem) => {
            const primitiveId = getPrimitiveId(primitiveItem);
            if (!primitiveId) {
              return;
            }
            existingCanvasPrimitiveIds.add(primitiveId);
            if (!stepIdByPrimitiveId.has(primitiveId)) {
              stepIdByPrimitiveId.set(primitiveId, stepId);
            }
          });
        });
        planStepPrimitiveById.forEach((primitiveItem, stepId) => {
          const primitiveId = getPrimitiveId(primitiveItem);
          if (primitiveId && !stepIdByPrimitiveId.has(primitiveId)) {
            stepIdByPrimitiveId.set(primitiveId, stepId);
          }
        });

        const adHocSummaryEntries = [];
        const adHocStepIdByPrimitiveId = new Map();
        directPlanQueries.forEach((queryPrimitive, idx) => {
          const summaryChildren = uniquePrimitives([
            ...(queryPrimitive?.primitives?.origin?.allSummary ?? EMPTY_ARRAY),
            ...(queryPrimitive?.primitives?.allSummary ?? EMPTY_ARRAY),
          ]).filter((summaryPrimitive) => summaryPrimitive?.id && summaryPrimitive?.type === "summary");

          summaryChildren.forEach((summaryPrimitive, summaryIdx) => {
            if (existingCanvasPrimitiveIds.has(summaryPrimitive.id)) {
              return;
            }
            const summaryStepId = `adhoc_summary_${summaryPrimitive.id}`;
            adHocStepIdByPrimitiveId.set(summaryPrimitive.id, summaryStepId);
            adHocSummaryEntries.push({
              stepId: summaryStepId,
              area: "analysis",
              order: 100000 + idx + ((summaryIdx + 1) * 0.01),
              name: summaryPrimitive?.title ?? "Query summary",
              description: "Summary from direct query",
              outputs: [summaryPrimitive],
              upstreamStepIds: [],
              upstreamInputLinks: [],
              preferredWidth: ARTEFACTS_CANVAS_SUMMARY_WIDTH,
              canvasVariant: "adhoc_summary",
              forceBand: "leaf",
            });
          });
        });

        const resolveStepIdFromPrimitiveId = (primitiveId) => {
          if (!primitiveId) {
            return null;
          }
          return (
            adHocStepIdByPrimitiveId.get(primitiveId) ??
            stepIdByPrimitiveId.get(primitiveId) ??
            null
          );
        };

        adHocSummaryEntries.forEach((entry) => {
          if (entry?.canvasVariant !== "adhoc_summary") {
            return;
          }
          const primaryPrimitive = entry?.outputs?.[0];
          const primitiveId = getPrimitiveId(primaryPrimitive);
          if (!primitiveId) {
            return;
          }
          const referenceParameters = primaryPrimitive?.referenceParameters ?? {};
          const diagnostics = referenceParameters?.diagnostics;
          const synthesisDebug = referenceParameters?.synthesis_debug;
          const synthesisStructuredSummary = referenceParameters?.synthesis_structured_summary;
          const synthesisInputCatalog = Array.isArray(synthesisDebug?.inputCatalog) ? synthesisDebug.inputCatalog : EMPTY_ARRAY;
          const synthesisInputIdToPrimitiveId = new Map(
            synthesisInputCatalog
              .map((entry) => [entry?.inputId, entry?.primitiveId])
              .filter(([inputId, primitiveId]) => typeof inputId === "string" && typeof primitiveId === "string"),
          );
          const linkedInputRefs = Array.from(
            new Set([
              ...collectSynthesisInputRefs(diagnostics),
              ...collectSynthesisInputRefs(synthesisDebug),
              ...collectSynthesisInputRefs(synthesisStructuredSummary),
            ].filter((id) => typeof id === "string" && id.length > 0)),
          );
          const linkedPrimitiveRefs = Array.from(
            new Set([
              ...collectSynthesisPrimitiveRefs(diagnostics),
              ...collectSynthesisPrimitiveRefs(synthesisDebug),
              ...collectSynthesisPrimitiveRefs(synthesisStructuredSummary),
            ].filter((id) => typeof id === "string" && id.length > 0)),
          );
          const upstreamStepIds = new Set(entry.upstreamStepIds ?? EMPTY_ARRAY);
          const upstreamInputLinks = new Map();
          const linkedPrimitiveIds = Array.from(
            new Set([
              ...linkedPrimitiveRefs,
              ...linkedInputRefs
                .map((inputRef) => synthesisInputIdToPrimitiveId.get(inputRef))
                .filter((id) => typeof id === "string" && id.length > 0),
            ]),
          );
          linkedPrimitiveIds.forEach((linkedPrimitiveId) => {
            const normalizedLinkedPrimitiveIds = new Set([
              linkedPrimitiveId,
              synthesisInputIdToPrimitiveId.get(linkedPrimitiveId),
            ]);
            normalizedLinkedPrimitiveIds.forEach((normalizedLinkedPrimitiveId) => {
              if (!normalizedLinkedPrimitiveId) {
                return;
              }
              const linkedStepId = resolveStepIdFromPrimitiveId(normalizedLinkedPrimitiveId);
              if (!linkedStepId || linkedStepId === entry.stepId) {
                return;
              }
              upstreamStepIds.add(linkedStepId);
            });
          });
          linkedInputRefs.forEach((inputRef) => {
            if (!inputRef || typeof inputRef !== "string") {
              return;
            }
            const mappedPrimitiveId = synthesisInputIdToPrimitiveId.get(inputRef);
            const linkedStepId = resolveStepIdFromPrimitiveId(mappedPrimitiveId);
            if (!linkedStepId || linkedStepId === entry.stepId) {
              return;
            }
            upstreamStepIds.add(linkedStepId);
            const key = `${linkedStepId}::${inputRef}`;
            if (!upstreamInputLinks.has(key)) {
              upstreamInputLinks.set(key, {
                stepId: linkedStepId,
                inputRef,
              });
            }
          });
          entry.upstreamStepIds = Array.from(upstreamStepIds);
          entry.upstreamInputLinks = Array.from(upstreamInputLinks.values());
        });

        adHocSummaryEntries.forEach((entry) => {
          if (entry?.stepId && !byStepId.has(entry.stepId)) {
            byStepId.set(entry.stepId, entry);
          }
        });
      }

      return Array.from(byStepId.values()).sort((a, b) => {
        const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
        const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
      });
    },
    [
      expandedWorkstreamStepScopeSet,
      focusedPlanStepScopeSet,
      planPrimitive,
      planPrimitiveIdToStepId,
      outputStepsByArea,
      outputStepsById,
      getPrimitiveById,
      planStepPrimitiveById,
      planStepSnapshots,
    ],
  );
  const hasArtefactsContent = useMemo(() => {
    if (selectedOutputView) {
      return true;
    }
    return artefactsCanvasEntries.length > 0;
  }, [artefactsCanvasEntries, selectedOutputView]);

  const handleCarouselShift = useCallback((stepId, delta, total) => {
    if (!total || total <= 1) {
      return;
    }
    setOutputCarouselIndexMap((prev) => {
      const current = prev[stepId] ?? 0;
      const nextIdx = (current + delta + total) % total;
      if (nextIdx === current) {
        return prev;
      }
      return {
        ...prev,
        [stepId]: nextIdx,
      };
    });
  }, []);
  const activeSubChatMode = selectedOutputView
    ? "output"
    : subChatOwner === "workstream" && selectedWorkstreamId
      ? "workstream"
      : null;
  const activeSubChatPrimitive =
    activeSubChatMode === "output" ? selectedStepPrimitive : workstreamChatPrimitive;
  const activeSubChatScope =
    activeSubChatMode === "output" ? effectiveSubChatScope : workstreamSubChatScope;
  const showSubChatPanel = activeSubChatMode === "workstream"
    ? Boolean(selectedWorkstreamId)
    : Boolean(activeSubChatPrimitive && activeSubChatScope);
  const subChatHasGuidance =
    activeSubChatMode === "output" ? Boolean(selectedStepSnapshot?.metadata?.subChat) : false;
  const isWideViewport = viewportWidth >= 1620;
  const agentPaneLayoutStyles = useMemo(() => {
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: "34%",
      maxWidth: "720px",
      minWidth: "320px",
    };
  }, []);
  const artefactsPaneLayoutStyles = useMemo(() => {
    return {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      maxWidth: "100%",
      minWidth: "320px",
    };
  }, []);
  const discoveryPaneLayoutStyles = useMemo(() => {
    if (discoveryPaneCollapsed) {
      return {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: "2.75rem",
        maxWidth: "2.75rem",
        minWidth: "2.75rem",
      };
    }
    return {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: isWideViewport ? "38rem" : "24rem",
      maxWidth: isWideViewport ? "42rem" : "26rem",
      minWidth: isWideViewport ? "24rem" : "20rem",
    };
  }, [discoveryPaneCollapsed, isWideViewport]);
  const containerInlineStyles = useMemo(
    () => ({
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      margin: "0 auto",
      transition: `max-width ${PANE_TRANSITION}`,
    }),
    [],
  );
  const containerClasses = "h-[calc(100vh_-_70px)] w-full bg-slate-50 p-4 dark:bg-slate-950";

  const artefactsPaneContent = selectedOutputView ? (
    <SelectedOutputDetail
      entry={selectedOutputView.entry}
      primitive={selectedOutputView.primitive}
      planStepIndexLookup={planStepIndexLookup}
      onBack={handleCloseOutputView}
      onOpenWorkspace={() => handleOpenWorkspace(selectedOutputView.primitive)}
      viewportWidth={viewportWidth}
      stepPrimitive={selectedStepPrimitive}
      stepSnapshot={selectedStepSnapshot}
    />
  ) : (
    <>
      {focusedPlanStep && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <button
            type="button"
            onClick={() => handlePlanStepFocus(null)}
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-blue-600 transition hover:text-blue-500 dark:text-blue-300 dark:hover:text-blue-200"
          >
            <span aria-hidden="true">←</span>
            Back to plan
          </button>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:bg-blue-900/40 dark:text-blue-200">
            {getPlanStepLabel(focusedPlanStep, planStepIndexLookup.get(focusedPlanStep.id) ?? 0)}
          </span>
        </div>
      )}
      {artefactsViewMode === "canvas" ? (
        <ArtefactsDependencyCanvas
          entries={artefactsCanvasEntries}
          outputCarouselIndexMap={outputCarouselIndexMap}
          onCardSelect={handleOutputCardSelect}
          onOpenWorkspace={handleOpenWorkspace}
          planStepInputsMap={planStepInputsMap}
          planStepParentLookup={planStepParentLookup}
          mainstore={mainstore}
        />
      ) : (
      TASK_SECTIONS.filter((section) => section.id !== "discovery").map((section) => {
        const idx = TASK_SECTIONS.findIndex((candidate) => candidate.id === section.id);
        const isDiscovery = section.id === "discovery";
        const isAnalysis = section.id === "analysis";
        const sectionKey = `section_${section.id}`;
        const expanded = expandedSections.has(sectionKey);
          const sectionOutputs = outputStepsByArea.get(section.id) ?? [];
          if (!isDiscovery && sectionOutputs.length === 0) {
            return null;
          }
          const handleSectionHeaderClick = () => {
            toggleSection(sectionKey);
            if (isAnalysis) {
              setShowAdvisorDropdown(false);
            }
          };
          const handleSectionHeaderKeyDown = (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleSectionHeaderClick();
            }
          };

          const sectionFlexBasis = expanded ? "0%" : "auto";
          return (
            <section
              key={section.id}
              className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
              style={{
                flexGrow: expanded ? 1 : 0,
                flexShrink: expanded ? 1 : 0,
                flexBasis: sectionFlexBasis,
              }}
            >
              <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                className="flex cursor-pointer select-none items-center justify-between border-b border-slate-100 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-slate-800 dark:bg-slate-900"
                onClick={handleSectionHeaderClick}
                onKeyDown={handleSectionHeaderKeyDown}
              >
                <div className="flex items-start gap-3">
                  <HeroIcon
                    icon="ChevronDownIcon"
                    className={`h-5 w-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                      {`Task ${idx + 1}`}
                    </span>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">{section.title}</h2>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {isDiscovery && (
                    <div
                      role="switch"
                      tabIndex={0}
                      aria-checked={discoveryActiveOnly}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        discoveryActiveOnly
                          ? "border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-400/60 dark:bg-blue-900/30 dark:text-blue-200"
                          : "border-transparent bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDiscoveryActiveToggle();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          handleDiscoveryActiveToggle();
                        }
                      }}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          discoveryActiveOnly ? "bg-blue-500" : "bg-slate-400 dark:bg-slate-600"
                        }`}
                      />
                      Show active only
                    </div>
                  )}
                  {isDiscovery && !expanded && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-200">
                        {discoverySummary.running && (
                          <svg
                            className="h-3 w-3 animate-spin text-blue-500"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                        <span>{discoverySummary.sources} data sources</span>
                      </div>
                      <div className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-200">
                        {discoverySummary.evidenceTotal} entities
                      </div>
                    </div>
                  )}
                  {isAnalysis && (
                    <div
                      role="switch"
                      tabIndex={0}
                      aria-checked={analysisLeafOnly}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        analysisLeafOnly
                          ? "border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-400/60 dark:bg-blue-900/30 dark:text-blue-200"
                          : "border-transparent bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleAnalysisLeafToggle();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          handleAnalysisLeafToggle();
                        }
                      }}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          analysisLeafOnly ? "bg-blue-500" : "bg-slate-400 dark:bg-slate-600"
                        }`}
                      />
                      Show leaf only
                    </div>
                  )}
                  {isAnalysis && (
                    <div
                      ref={advisorDropdownRef}
                      className={`relative ${advisorSuggestionLog.length ? "" : "opacity-60"}`}
                      role="button"
                      tabIndex={advisorSuggestionLog.length ? 0 : -1}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!advisorSuggestionLog.length) {
                          return;
                        }
                        setShowAdvisorDropdown((prev) => !prev);
                      }}
                      onKeyDown={(event) => {
                        if (!advisorSuggestionLog.length) {
                          return;
                        }
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          setShowAdvisorDropdown((prev) => !prev);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2 rounded-full border border-purple-200 bg-white/80 px-3 py-1 text-xs font-semibold text-purple-700 shadow-sm dark:border-purple-400/40 dark:bg-purple-900/30 dark:text-purple-200">
                        <HeroIcon icon="SparklesIcon" className="h-4 w-4" />
                        <span>Advisor suggestions</span>
                        <span className="text-[11px] font-semibold text-purple-500 dark:text-purple-200">
                          {advisorSuggestionLog.length}
                        </span>
                        <HeroIcon
                          icon="ChevronDownIcon"
                          className={`h-3 w-3 transition-transform ${showAdvisorDropdown ? "rotate-180" : ""}`}
                        />
                      </div>
                      {showAdvisorDropdown && (
                        <div className="absolute right-0 z-20 mt-2 w-80 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                          {advisorSuggestionLog.length === 0 ? (
                            <p className="text-xs text-slate-500 dark:text-slate-300">
                              Advisor suggestions will appear here.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {advisorSuggestionLog.map((suggestion) => (
                                <div
                                  key={suggestion.id}
                                  className="rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60"
                                >
                                  <div className="text-[11px] font-semibold uppercase tracking-wide text-purple-500 dark:text-purple-300">
                                    {formatAdvisorTypeLabel(suggestion.type)}
                                  </div>
                                  <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {suggestion.label || suggestion.optionId || "Advisor suggestion"}
                                  </div>
                                  {suggestion.message && (
                                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                                      {suggestion.message}
                                    </p>
                                  )}
                                  {suggestion.id && (
                                    <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                                      Reply with{" "}
                                      <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px] dark:bg-white/10">{`advisor accept ${suggestion.id}`}</code>{" "}
                                      to run.
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {expanded && (
                <div className="flex-1 min-h-0">
                  <div className="h-full overflow-y-auto px-4 py-3">
                {isDiscovery && (
                        <div className="flex flex-col gap-3">
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                              Searches, queries & summaries
                            </p>
                            {focusedDiscoveryContent.length > 0 ? (
                              <div
                                className="grid gap-2"
                                style={{ gridTemplateColumns: `repeat(${gridColumnCount}, minmax(0, 1fr))` }}
                              >
                                {discoveryRows.map((row, rowIndex) => (
                                  <Fragment key={`discovery_row_${rowIndex}`}>
                                    {row.map((group) => {
                                      const isSelected =
                                        selectedDataGroup?.kind === "discovery" && selectedDataGroup?.title === group.title;
                                      const hasActiveItems = group.items?.some?.(isPrimitiveActive);
                                      return (
                                        <button
                                          type="button"
                                          key={`discovery_${group.title}`}
                                          onClick={() => handleDataGroupSelect("discovery", group.title)}
                                          className={`relative flex flex-col gap-1 border px-3 text-left text-xs transition hover:border-slate-300 dark:hover:border-white/30 ${
                                            isSelected
                                              ? "rounded-2xl rounded-b-none border-blue-200 border-b-transparent bg-blue-50 py-3 dark:border-transparent dark:bg-blue-900/40"
                                              : "rounded-2xl border-slate-200 bg-white py-2 dark:border-slate-700 dark:bg-slate-900"
                                          }`}
                                          style={isSelected ? { marginBottom: "-0.5rem" } : undefined}
                                        >
                                          {isSelected && (
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleCloseSelectedDataGroup();
                                              }}
                                              className="absolute bottom-1 left-1/2 z-[1] inline-flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:text-slate-700 dark:bg-slate-900/80 dark:text-slate-200 dark:ring-white/20 dark:hover:bg-slate-900"
                                              aria-label="Close details"
                                            >
                                              <HeroIcon icon="XMarkIcon" className="h-3.5 w-3.5" />
                                            </button>
                                          )}
                                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                                            <div className="flex flex-wrap items-baseline gap-2">
                                              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                {group.title}
                                              </span>
                                              <span className="text-[11px] text-slate-500 dark:text-slate-300">
                                                View individual records
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              {hasActiveItems && (
                                                <div className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-300">
                                                  <svg
                                                    className="h-3 w-3 animate-spin text-blue-500"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <circle
                                                      className="opacity-25"
                                                      cx="12"
                                                      cy="12"
                                                      r="10"
                                                      stroke="currentColor"
                                                      strokeWidth="4"
                                                    />
                                                    <path
                                                      className="opacity-75"
                                                      fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    />
                                                  </svg>
                                                  Running
                                                </div>
                                              )}
                                              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-300">
                                                {group.count}
                                              </span>
                                            </div>
                                          </div>
                                          <span className="text-[11px] text-slate-500 dark:text-slate-300">
                                            {group.count} active primitives
                                          </span>
                                        </button>
                                      );
                                    })}
                                    {selectedDiscoveryRowIndex === rowIndex &&
                                      selectedDataGroup?.kind === "discovery" &&
                                      renderSelectedDataGroupDrawer(`discovery_drawer_${rowIndex}`, {
                                        disableLeftRadius: selectedDiscoveryColumnIndex === 0,
                                        disableRightRadius: selectedDiscoveryColumnIndex === row.length - 1,
                                      })}
                                  </Fragment>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 dark:text-slate-300">
                                {discoveryActiveOnly
                                  ? "No active searches are running right now."
                                  : "No searches or queries have been run yet."}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                              Entities & results
                            </p>
                            {focusedEvidenceContent.length > 0 ? (
                              <div
                                className="grid gap-2"
                                style={{ gridTemplateColumns: `repeat(${gridColumnCount}, minmax(0, 1fr))` }}
                              >
                                {evidenceRows.map((row, rowIndex) => (
                                  <Fragment key={`evidence_row_${rowIndex}`}>
                                    {row.map((group) => {
                                      const isSelected =
                                        selectedDataGroup?.kind === "evidence" && selectedDataGroup?.title === group.title;
                                      const previewItems = (group.items ?? []).slice(0, 6);
                                      const remaining = Math.max(0, (group.items?.length ?? 0) - previewItems.length);
                                      return (
                                        <button
                                          type="button"
                                          key={`evidence_${group.title}`}
                                          onClick={() => handleDataGroupSelect("evidence", group.title)}
                                          className={`relative flex flex-col gap-1 border px-3 text-left text-xs transition hover:border-slate-300 dark:hover:border-white/30 ${
                                            isSelected
                                              ? "rounded-2xl rounded-b-none border-blue-200 border-b-transparent bg-blue-50 py-3 dark:border-transparent dark:bg-blue-900/40"
                                              : "rounded-2xl border-slate-200 bg-white py-2 dark:border-slate-700 dark:bg-slate-900"
                                          }`}
                                          style={isSelected ? { marginBottom: "-0.5rem" } : undefined}
                                        >
                                          {isSelected && (
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                handleCloseSelectedDataGroup();
                                              }}
                                              className="absolute bottom-1 left-1/2 z-[1] inline-flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:text-slate-700 dark:bg-slate-900/80 dark:text-slate-200 dark:ring-white/20 dark:hover:bg-slate-900"
                                              aria-label="Close details"
                                            >
                                              <HeroIcon icon="XMarkIcon" className="h-3.5 w-3.5" />
                                            </button>
                                          )}
                                          <div className="flex items-center gap-2">
                                            <div className="flex flex-1 items-center gap-2">
                                              {group.items?.[0]?.metadata?.icon && (
                                                <HeroIcon
                                                  icon={group.items[0].metadata.icon}
                                                  className="h-4 w-4 text-slate-500 dark:text-slate-300"
                                                />
                                              )}
                                              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                {group.items?.[0]?.metadata?.plurals ||
                                                  group.items?.[0]?.metadata?.plural ||
                                                  (group.title?.endsWith("s") ? group.title : `${group.title ?? ""}s`)}
                                              </span>
                                              <div className="ml-auto flex items-center">
                                                {previewItems.map((item, idx) => (
                                                  <Avatar
                                                    key={item.id ?? item._id ?? idx}
                                                    src={resolvePreviewAvatarUrl(item)}
                                                    name={item.title || item.metadata?.title || "item"}
                                                    showFallback
                                                    isBordered
                                                    className="h-6 w-6 text-tiny font-semibold text-slate-600 dark:border-slate-900 dark:bg-slate-800 dark:text-slate-200"
                                                    imgProps={{ referrerPolicy: "no-referrer" }}
                                                    style={{ marginLeft: idx === 0 ? 0 : "-0.3rem" }}
                                                  />
                                                ))}
                                                {remaining > 0 && (
                                                  <Avatar
                                                    isBordered
                                                    name={`+${remaining}`}
                                                    className="h-6 w-6 bg-slate-200 text-tiny font-semibold text-slate-600 dark:border-slate-900 dark:bg-slate-800 dark:text-slate-200"
                                                    style={{ marginLeft: previewItems.length === 0 ? 0 : "-0.3rem" }}
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-300">
                                            <span>{group.count} evidence items</span>
                                            <span>Click to preview</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                    {selectedEvidenceRowIndex === rowIndex &&
                                      selectedDataGroup?.kind === "evidence" &&
                                      renderSelectedDataGroupDrawer(`evidence_drawer_${rowIndex}`, {
                                        disableLeftRadius: selectedEvidenceColumnIndex === 0,
                                        disableRightRadius: selectedEvidenceColumnIndex === row.length - 1,
                                      })}
                                  </Fragment>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 dark:text-slate-300">No evidence collected yet.</p>
                            )}
                          </div>
                        </div>
                      )}
                {sectionOutputs.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {sectionOutputs.map((entry) => {
                      const outputs = entry.outputs ?? [];
                      const total = outputs.length;
                      const currentIndex = outputCarouselIndexMap[entry.stepId] ?? 0;
                      const activePrimitive = outputs[currentIndex] ?? outputs[0];
                      const stepPrimitive = planStepPrimitiveById.get(entry.stepId) ?? null;
                      const stepSnapshot = planStepSnapshotById.get(entry.stepId) ?? null;
                      const contentHints = resolveStepContentHints({
                        stepSnapshot,
                        stepPrimitive,
                      });
                      const contentMarkdown = buildContentMarkdownTable(
                        activePrimitive?.itemsForProcessing,
                        contentHints,
                      );
                      const analysisRunning =
                        stepSnapshot?.status === "in_progress" ||
                        stepPrimitive?.referenceParameters?.status === "in_progress" ||
                        stepPrimitive?.executionState?.status === "in_progress";
                      const storedPreviewSelection =
                        typeof stepPrimitive?.referenceParameters?.outputSelection === "string"
                          ? stepPrimitive.referenceParameters.outputSelection
                          : "Outputs";
                      const previewOutputNames = storedPreviewSelection ? [storedPreviewSelection] : undefined;
                      return (
                        <Card
                          key={entry.stepId}
                          isPressable
                          isHoverable
                          onPress={() => handleOutputCardSelect(entry, activePrimitive)}
                          className="w-full cursor-pointer border border-slate-200 bg-white/90 shadow-sm transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/70 md:w-[420px] lg:w-[460px]"
                        >
                          <CardHeader className="flex flex-col items-start gap-1 px-4 pt-4">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                              {analysisRunning && (
                                <svg
                                  className="h-4 w-4 animate-spin text-blue-500"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              )}
                              <span>{entry.name}</span>
                            </div>
                            {selectedWorkstream && (
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-900/40 dark:text-emerald-200">
                                {selectedWorkstream.title ?? "Workstream"}
                              </span>
                            )}
                            {entry.description && (
                              <p className="text-xs text-slate-500 dark:text-slate-300">{entry.description}</p>
                            )}
                          </CardHeader>
                          <CardBody className="flex flex-col items-center gap-3 pb-4">
                            {activePrimitive ? (
                              contentMarkdown ? (
                                <MarkdownTablePreview markdown={contentMarkdown} width={360} className="w-full" />
                              ) : (
                                <KonvaPrimitive
                                  key={`${entry.stepId}_${activePrimitive.id}_${previewOutputNames?.[0] ?? "all"}`}
                                  primitive={activePrimitive}
                                  width={360}
                                  flowOutputNames={previewOutputNames}
                                />
                              )
                            ) : (
                              <div className="w-full rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-300">
                                Output not available yet.
                              </div>
                            )}
                            {total > 1 && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleCarouselShift(entry.stepId, -1, total)}
                                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 dark:border-slate-600 dark:text-slate-200"
                                >
                                  Previous
                                </button>
                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-300">
                                  {currentIndex + 1} / {total}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleCarouselShift(entry.stepId, 1, total)}
                                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 dark:border-slate-600 dark:text-slate-200"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                            {selectedWorkstreamParent && activePrimitive && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handlePromoteOutput(activePrimitive);
                                }}
                                className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:text-emerald-800 dark:border-emerald-500/50 dark:text-emerald-200"
                              >
                                Promote to final
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenWorkspace(activePrimitive);
                              }}
                              className="rounded-full border border-slate-200 px-4 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 dark:border-slate-600 dark:text-slate-200"
                            >
                              Open in workspace
                            </button>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                )}
                  </div>
                </div>
              )}
            </section>
          );
      }))}
    </>
  );
  const discoveryPaneContent = discoveryPaneCollapsed ? (
    <button
      type="button"
      onClick={() => setDiscoveryPaneCollapsed(false)}
      className="flex h-full w-full items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition hover:text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
      title="Expand Data & Discovery"
      aria-label="Expand Data & Discovery pane"
    >
      <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] font-semibold uppercase tracking-wide">
        Data & Discovery
      </span>
    </button>
  ) : (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-3 dark:border-slate-800">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
            Data & Discovery
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Sources, queries, and entities</div>
        </div>
        <button
          type="button"
          onClick={() => setDiscoveryPaneCollapsed(true)}
          className="rounded-full border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 transition hover:border-slate-300 hover:text-slate-700 dark:border-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
        >
          Hide
        </button>
      </div>
      <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        <div
          role="switch"
          tabIndex={0}
          aria-checked={discoveryActiveOnly}
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition ${
            discoveryActiveOnly
              ? "border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-400/60 dark:bg-blue-900/30 dark:text-blue-200"
              : "border-transparent bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
          }`}
          onClick={handleDiscoveryActiveToggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleDiscoveryActiveToggle();
            }
          }}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              discoveryActiveOnly ? "bg-blue-500" : "bg-slate-400 dark:bg-slate-600"
            }`}
          />
          Show active only
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="flex flex-col gap-3">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Searches, queries & summaries
            </p>
            {focusedDiscoveryContent.length > 0 ? (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${gridColumnCount}, minmax(0, 1fr))` }}
              >
                {discoveryRows.map((row, rowIndex) => (
                  <Fragment key={`discovery_pane_row_${rowIndex}`}>
                    {row.map((group) => {
                      const isSelected =
                        selectedDataGroup?.kind === "discovery" && selectedDataGroup?.title === group.title;
                      const hasActiveItems = group.items?.some?.(isPrimitiveActive);
                      return (
                        <button
                          type="button"
                          key={`discovery_pane_${group.title}`}
                          onClick={() => handleDataGroupSelect("discovery", group.title)}
                          className={`relative flex flex-col gap-1 border px-3 text-left text-xs transition hover:border-slate-300 dark:hover:border-white/30 ${
                            isSelected
                              ? "rounded-2xl rounded-b-none border-blue-200 border-b-transparent bg-blue-50 py-3 dark:border-transparent dark:bg-blue-900/40"
                              : "rounded-2xl border-slate-200 bg-white py-2 dark:border-slate-700 dark:bg-slate-900"
                          }`}
                          style={isSelected ? { marginBottom: "-0.5rem" } : undefined}
                        >
                          {isSelected && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCloseSelectedDataGroup();
                              }}
                              className="absolute bottom-1 left-1/2 z-[1] inline-flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:text-slate-700 dark:bg-slate-900/80 dark:text-slate-200 dark:ring-white/20 dark:hover:bg-slate-900"
                              aria-label="Close details"
                            >
                              <HeroIcon icon="XMarkIcon" className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {group.title}
                            </span>
                            <div className="flex items-center gap-2">
                              {hasActiveItems && (
                                <div className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-300">
                                  <svg
                                    className="h-3 w-3 animate-spin text-blue-500"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Running
                                </div>
                              )}
                              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-300">
                                {group.count}
                              </span>
                            </div>
                          </div>
                          <span className="text-[11px] text-slate-500 dark:text-slate-300">
                            {group.count} active primitives
                          </span>
                        </button>
                      );
                    })}
                    {selectedDiscoveryRowIndex === rowIndex &&
                      selectedDataGroup?.kind === "discovery" &&
                      renderSelectedDataGroupDrawer(`discovery_pane_drawer_${rowIndex}`, {
                        disableLeftRadius: selectedDiscoveryColumnIndex === 0,
                        disableRightRadius: selectedDiscoveryColumnIndex === row.length - 1,
                      })}
                  </Fragment>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-300">
                {discoveryActiveOnly
                  ? "No active searches are running right now."
                  : "No searches or queries have been run yet."}
              </p>
            )}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Entities & results
            </p>
            {focusedEvidenceContent.length > 0 ? (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${gridColumnCount}, minmax(0, 1fr))` }}
              >
                {evidenceRows.map((row, rowIndex) => (
                  <Fragment key={`evidence_pane_row_${rowIndex}`}>
                    {row.map((group) => {
                      const isSelected =
                        selectedDataGroup?.kind === "evidence" && selectedDataGroup?.title === group.title;
                      const previewItems = (group.items ?? []).slice(0, 6);
                      const remaining = Math.max(0, (group.items?.length ?? 0) - previewItems.length);
                      return (
                        <button
                          type="button"
                          key={`evidence_pane_${group.title}`}
                          onClick={() => handleDataGroupSelect("evidence", group.title)}
                          className={`relative flex flex-col gap-1 border px-3 text-left text-xs transition hover:border-slate-300 dark:hover:border-white/30 ${
                            isSelected
                              ? "rounded-2xl rounded-b-none border-blue-200 border-b-transparent bg-blue-50 py-3 dark:border-transparent dark:bg-blue-900/40"
                              : "rounded-2xl border-slate-200 bg-white py-2 dark:border-slate-700 dark:bg-slate-900"
                          }`}
                          style={isSelected ? { marginBottom: "-0.5rem" } : undefined}
                        >
                          {isSelected && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCloseSelectedDataGroup();
                              }}
                              className="absolute bottom-1 left-1/2 z-[1] inline-flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:text-slate-700 dark:bg-slate-900/80 dark:text-slate-200 dark:ring-white/20 dark:hover:bg-slate-900"
                              aria-label="Close details"
                            >
                              <HeroIcon icon="XMarkIcon" className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div className="flex items-center gap-2">
                            <div className="flex flex-1 items-center gap-2">
                              {group.items?.[0]?.metadata?.icon && (
                                <HeroIcon
                                  icon={group.items[0].metadata.icon}
                                  className="h-4 w-4 text-slate-500 dark:text-slate-300"
                                />
                              )}
                              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {group.items?.[0]?.metadata?.plurals ||
                                  group.items?.[0]?.metadata?.plural ||
                                  (group.title?.endsWith("s") ? group.title : `${group.title ?? ""}s`)}
                              </span>
                              <div className="ml-auto flex items-center">
                                {previewItems.map((item, idx) => (
                                  <Avatar
                                    key={item.id ?? item._id ?? idx}
                                    src={resolvePreviewAvatarUrl(item)}
                                    name={item.title || item.metadata?.title || "item"}
                                    showFallback
                                    isBordered
                                    className="h-6 w-6 text-tiny font-semibold text-slate-600 dark:border-slate-900 dark:bg-slate-800 dark:text-slate-200"
                                    imgProps={{ referrerPolicy: "no-referrer" }}
                                    style={{ marginLeft: idx === 0 ? 0 : "-0.3rem" }}
                                  />
                                ))}
                                {remaining > 0 && (
                                  <Avatar
                                    isBordered
                                    name={`+${remaining}`}
                                    className="h-6 w-6 bg-slate-200 text-tiny font-semibold text-slate-600 dark:border-slate-900 dark:bg-slate-800 dark:text-slate-200"
                                    style={{ marginLeft: previewItems.length === 0 ? 0 : "-0.3rem" }}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-300">
                            <span>{group.count} evidence items</span>
                            <span>Click to preview</span>
                          </div>
                        </button>
                      );
                    })}
                    {selectedEvidenceRowIndex === rowIndex &&
                      selectedDataGroup?.kind === "evidence" &&
                      renderSelectedDataGroupDrawer(`evidence_pane_drawer_${rowIndex}`, {
                        disableLeftRadius: selectedEvidenceColumnIndex === 0,
                        disableRightRadius: selectedEvidenceColumnIndex === row.length - 1,
                      })}
                  </Fragment>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-300">No evidence collected yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className={containerClasses}>
      <div className="flex h-full w-full gap-3" style={containerInlineStyles}>
        <div
          style={{
            ...agentPaneLayoutStyles,
            transition: `flex-basis ${PANE_TRANSITION}, max-width ${PANE_TRANSITION}`,
          }}
          className="flex h-full min-w-[320px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-colors dark:border-slate-700 dark:bg-slate-900"
        >
        {workstreamRows.length > 0 && (
          <div className="border-b border-slate-200 px-3 py-3 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setWorkstreamListCollapsed((prev) => !prev)}
                className="text-xs font-semibold uppercase tracking-wide text-slate-400 transition hover:text-slate-500 dark:text-slate-300"
              >
                Workstreams
              </button>
              <div className="flex items-center gap-2">
                {primitive?.id ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/research/${primitive.id}`)}
                    className="text-[11px] font-semibold text-indigo-600 transition hover:text-indigo-500 dark:text-indigo-300"
                  >
                    Open research
                  </button>
                ) : null}
                {workstreamRows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowArchivedGoals((prev) => !prev)}
                    className="text-[11px] font-semibold text-slate-500 transition hover:text-slate-700 dark:text-slate-300"
                  >
                    {showArchivedGoals ? "Hide archived" : "Show archived"}
                  </button>
                )}
                {selectedWorkstream && (
                  <button
                    type="button"
                    onClick={() => handleWorkstreamSelect(null)}
                    className="text-[11px] font-semibold text-blue-600 transition hover:text-blue-500 dark:text-blue-300"
                  >
                    Clear
                  </button>
                )}
                {workstreamRows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setWorkstreamListCollapsed((prev) => !prev)}
                    className="text-[11px] font-semibold text-slate-500 transition hover:text-slate-700 dark:text-slate-300"
                  >
                    {workstreamListCollapsed ? "Expand" : "Collapse"}
                  </button>
                )}
              </div>
            </div>
            {!workstreamListCollapsed && workstreamRows.length > 0 && (
              <div className="mt-3 flex flex-col gap-3 overflow-auto" style={{ maxHeight: "30vh" }}>
                {workstreamRows.map((row) => {
                  const workstream = row.workstream;
                  const id = getPrimitiveId(workstream);
                  const isActive = id && id === selectedWorkstreamId;
                  const isArchived =
                    String(workstream?.referenceParameters?.status ?? "").toLowerCase() === "archived";
                  const goalId =
                    workstream?.referenceParameters?.goalId ??
                    workstream?.referenceParameters?.metadata?.goalId ??
                    null;
                  return (
                    <button
                      key={id ?? workstream?.title}
                      type="button"
                      onClick={() => handleWorkstreamSelect(id)}
                      className={`group relative flex items-center justify-between gap-3 overflow-hidden rounded-xl border px-3 py-3 text-left text-[13px] font-semibold transition shadow-sm ${
                        isActive
                          ? "border-emerald-300 bg-gradient-to-r from-emerald-50 via-emerald-50 to-white text-emerald-800 shadow-emerald-100 dark:border-emerald-400/50 dark:from-emerald-500/10 dark:via-emerald-500/5 dark:to-slate-900 dark:text-emerald-200"
                          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      }`}
                      style={{ paddingLeft: `${8 + row.depth * 12}px` }}
                    >
                      <div className="absolute inset-0 pointer-events-none">
                        <div
                          className={`h-full w-1.5 ${
                            isActive
                              ? "bg-emerald-400/70"
                              : "bg-transparent group-hover:bg-slate-200/70 dark:group-hover:bg-slate-700/60"
                          }`}
                        />
                      </div>
                      <div className="relative flex min-w-0 items-center gap-3">
                        <div
                          className={`flex h-9 w-9 items-center justify-center rounded-full border ${
                            isActive
                              ? "border-emerald-200 bg-emerald-100 text-emerald-600 dark:border-emerald-400/40 dark:bg-emerald-500/20 dark:text-emerald-200"
                              : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          <HeroIcon icon="SparklesIcon" className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate">
                            {workstream?.title ?? workstream?.referenceParameters?.title ?? "Workstream"}
                          </div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-400">
                            Goal workstream
                          </div>
                        </div>
                      </div>
                      {isActive && (
                        <span className="relative inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          Current
                        </span>
                      )}
                      {!isActive && isArchived && (
                        <span className="relative inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
                          Archived
                        </span>
                      )}
                      {goalId && (
                        <button
                          type="button"
                          title="Delete goal"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!goalId) {
                              return;
                            }
                            setGoalToDelete({
                              id: goalId,
                              goalId,
                              label: workstream?.title ?? workstream?.referenceParameters?.title ?? "Goal",
                              workstreamId: id ?? null,
                            });
                            setShowGoalDeleteConfirm(true);
                          }}
                          className="relative ml-2 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-rose-300 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <HeroIcon icon="TrashIcon" className="h-4 w-4" />
                        </button>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="relative h-full w-full overflow-hidden">
          <motion.div
            className="absolute inset-0 z-10 flex flex-col overflow-hidden bg-slate-50/60 dark:bg-slate-900/60"
            animate={{ x: showSubChatPanel ? "0%" : "100%" }}
            initial={false}
            transition={{ duration: 0.16, ease: "easeInOut" }}
            style={{ pointerEvents: showSubChatPanel ? "auto" : "none" }}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-300">
              <button
                type="button"
                onClick={activeSubChatMode === "workstream" ? handleCloseWorkstreamChat : handleCloseOutputView}
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-blue-600 transition hover:text-blue-500 dark:text-blue-300"
              >
                <span aria-hidden="true">←</span>
                Back to plan chat
              </button>
              <span className="text-[10px] text-slate-400">
                {activeSubChatMode === "workstream"
                  ? selectedWorkstream?.title ?? "Workstream"
                  : selectedOutputView?.entry?.name ?? "Visualization"}
              </span>
            </div>
            {!subChatHasGuidance && (
              <div className="px-4 py-2 text-[11px] text-slate-500 dark:text-slate-300">
                No preset edit instructions yet—use this chat to request scoped changes, and I’ll wire them through
                the workflow.
              </div>
            )}
            <div className="flex-1 overflow-hidden p-2">
              <div className="h-full rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                {activeSubChatPrimitive ? (
                  <AgentChat
                    primitive={activeSubChatPrimitive}
                    scope={activeSubChatScope}
                    seperateInput={true}
                    showPlan={activeSubChatMode === "workstream"}
                    smallFont={true}
                    hideChatSession={false}
                    hideChatUpdatedTimestamp={true}
                    onChatSessionChange={setSubChatSessionInfo}
                  />
                ) : activeSubChatMode === "workstream" ? (
                  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500 dark:text-slate-300">
                    Preparing workstream chat…
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500 dark:text-slate-300">
                    Select an output to start a focused conversation.
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          <motion.div
            className="absolute inset-0 flex-1 overflow-hidden p-2"
            animate={{ x: showSubChatPanel ? "-100%" : "0%" }}
            initial={false}
            transition={{ duration: 0.16, ease: "easeInOut" }}
            style={{ pointerEvents: showSubChatPanel ? "none" : "auto" }}
          >
            <AgentChat
              ref={mainAgentRef}
              setStatus={setAgentStatus}
              primitive={primitive}
              seperateInput={true}
              showPlan={true}
              smallFont={true}
              hideChatSession={false}
              hideChatUpdatedTimestamp={true}
              onTaskAreaChange={setActiveTaskArea}
              onPlanPrimitiveChange={setPlanPrimitiveId}
              onAdvisorSuggestionsChange={handleAdvisorSuggestionsChange}
              focusedPlanStepId={focusedPlanStepId}
              onPlanStepFocus={handlePlanStepFocus}
              selectedWorkstreamId={selectedWorkstreamId}
              onWorkstreamSelect={handleWorkstreamSelect}
              {...props}
            />
          </motion.div>
        </div>
        </div>
        {hasArtefactsContent && (
          <div
            style={{
              ...artefactsPaneLayoutStyles,
              transition: `flex-basis ${PANE_TRANSITION}, max-width ${PANE_TRANSITION}, min-width ${PANE_TRANSITION}`,
            }}
            className={`flex min-h-0 min-w-0 flex-col gap-3 ${
              artefactsViewMode === "canvas" ? "overflow-hidden" : "overflow-auto"
            }`}
          >
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Artefacts
                </div>
                {!selectedOutputView && (
                  <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800">
                    <button
                      type="button"
                      onClick={() => setArtefactsViewMode("card")}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                        artefactsViewMode === "card"
                          ? "bg-white text-slate-700 shadow-sm dark:bg-slate-700 dark:text-slate-100"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
                      }`}
                    >
                      Card
                    </button>
                    <button
                      type="button"
                      onClick={() => setArtefactsViewMode("canvas")}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                        artefactsViewMode === "canvas"
                          ? "bg-white text-slate-700 shadow-sm dark:bg-slate-700 dark:text-slate-100"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
                      }`}
                    >
                      Canvas
                    </button>
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Analysis and visualization outputs
              </div>
            </div>
            {artefactsPaneContent}
          </div>
        )}
        <div
          style={{
            ...discoveryPaneLayoutStyles,
            transition: `flex-basis ${PANE_TRANSITION}, max-width ${PANE_TRANSITION}, min-width ${PANE_TRANSITION}`,
          }}
          className="flex min-w-0 flex-col gap-3 overflow-hidden"
          ref={handleRightPaneRef}
        >
          {!discoveryPaneCollapsed && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <button
                type="button"
                onClick={() => setGlobalContextCollapsed((prev) => !prev)}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <span className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  <HeroIcon icon="GlobeAltIcon" className="h-3.5 w-3.5 flex-shrink-0" />
                  Global context
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-400">
                  {globalContextEntries.length} {globalContextEntries.length === 1 ? "value" : "values"}
                  <HeroIcon
                    icon="ChevronDownIcon"
                    className={`h-3.5 w-3.5 transition ${globalContextCollapsed ? "" : "rotate-180"}`}
                  />
                </span>
              </button>
              {!globalContextCollapsed && (
                <div className="mt-2 flex max-h-24 flex-col gap-1 overflow-y-auto pr-1">
                  {globalContextEntries.length ? (
                    globalContextEntries.map((entry) => {
                      const displayValue =
                        entry.value.length > 180 ? `${entry.value.slice(0, 177)}...` : entry.value;
                      return (
                        <div
                          key={entry.key}
                          className="flex items-start gap-1.5 text-[11px] leading-4 text-slate-600 dark:text-slate-200"
                        >
                          <span className="flex-shrink-0 font-semibold text-slate-400 dark:text-slate-400">
                            {entry.label}:
                          </span>
                          <span className="min-w-0 break-words" title={entry.value}>
                            {displayValue}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-[11px] text-slate-500 dark:text-slate-300">
                      No global context values yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {discoveryPaneContent}
        </div>
      </div>
      {showGoalDeleteConfirm && (
        <ConfirmationPopup
          title="Delete goal?"
          message="This will remove the goal and its steps that are not used elsewhere. This can't be undone."
          confirmText="Delete goal"
          confirmColor="danger"
          confirm={handleDeleteGoal}
          cancel={() => setShowGoalDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

function SelectedOutputHeader({ entry, planStepIndexLookup, onBack, onOpenWorkspace }) {
  const stepIndex =
    typeof entry.order === "number" ? entry.order : planStepIndexLookup.get(entry.stepId);
  const taskLabel = typeof stepIndex === "number" ? `Task ${stepIndex + 1}` : "Selected output";
  return (
    <div className="flex flex-wrap items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-300">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-blue-600 transition hover:text-blue-500 dark:text-blue-300"
        >
          <span aria-hidden="true">←</span>
          Back to plan
        </button>
        <span className="text-slate-300 dark:text-slate-600">|</span>
        <span>{taskLabel}</span>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <span className="text-slate-900 dark:text-white">{entry.name}</span>
      </div>
      <div className="flex items-center gap-2">
        {entry.description && (
          <span className="text-xs text-slate-500 dark:text-slate-400">{entry.description}</span>
        )}
        <button
          type="button"
          onClick={onOpenWorkspace}
          className="rounded-full border border-slate-200 px-4 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 dark:border-slate-600 dark:text-slate-100"
        >
          Open in workspace
        </button>
      </div>
    </div>
  );
}

function areCanvasEntriesEqual(prevEntries, nextEntries) {
  const prev = Array.isArray(prevEntries) ? prevEntries : EMPTY_ARRAY;
  const next = Array.isArray(nextEntries) ? nextEntries : EMPTY_ARRAY;
  if (prev.length !== next.length) {
    return false;
  }
  for (let idx = 0; idx < prev.length; idx += 1) {
    const prevEntry = prev[idx];
    const nextEntry = next[idx];
    if (!prevEntry || !nextEntry) {
      return false;
    }
    if (
      prevEntry.stepId !== nextEntry.stepId ||
      prevEntry.order !== nextEntry.order ||
      prevEntry.name !== nextEntry.name
    ) {
      return false;
    }
    const prevOutputs = Array.isArray(prevEntry.outputs) ? prevEntry.outputs : EMPTY_ARRAY;
    const nextOutputs = Array.isArray(nextEntry.outputs) ? nextEntry.outputs : EMPTY_ARRAY;
    if (prevOutputs.length !== nextOutputs.length) {
      return false;
    }
    for (let outIdx = 0; outIdx < prevOutputs.length; outIdx += 1) {
      if (prevOutputs[outIdx]?.id !== nextOutputs[outIdx]?.id) {
        return false;
      }
    }
  }
  return true;
}

function areStepDependencyMapsEqual(prevProps, nextProps) {
  const stepIds = (nextProps.entries ?? EMPTY_ARRAY).map((entry) => entry?.stepId).filter(Boolean);
  if (!stepIds.length) {
    return true;
  }
  for (const stepId of stepIds) {
    if (prevProps.planStepParentLookup.get(stepId) !== nextProps.planStepParentLookup.get(stepId)) {
      return false;
    }
    const prevInputs = prevProps.planStepInputsMap.get(stepId) ?? EMPTY_ARRAY;
    const nextInputs = nextProps.planStepInputsMap.get(stepId) ?? EMPTY_ARRAY;
    if (prevInputs.length !== nextInputs.length) {
      return false;
    }
    for (let idx = 0; idx < nextInputs.length; idx += 1) {
      if (prevInputs[idx] !== nextInputs[idx]) {
        return false;
      }
    }
  }
  return true;
}

const ArtefactsDependencyCanvas = memo(function ArtefactsDependencyCanvas({
  entries,
  outputCarouselIndexMap,
  onCardSelect,
  onOpenWorkspace,
  planStepInputsMap,
  planStepParentLookup,
  mainstore,
}) {
  const boardStateRef = useRef({});
  const canvasRef = useRef(null);
  const frameDataByIdRef = useRef(new Map());
  const onCardSelectRef = useRef(onCardSelect);
  const onOpenWorkspaceRef = useRef(onOpenWorkspace);
  boardStateRef.current.renderSubPages = false;
  boardStateRef.current.hideWidgets = true;
  onCardSelectRef.current = onCardSelect;
  onOpenWorkspaceRef.current = onOpenWorkspace;

  const graph = useMemo(() => {
    const nodeEntries = Array.isArray(entries) ? entries.filter((entry) => entry?.stepId) : [];
    if (!nodeEntries.length) {
      return { nodes: [], edges: [] };
    }
    const byStepId = new Map(nodeEntries.map((entry) => [entry.stepId, entry]));
    const edgeKeys = new Set();
    const predecessors = new Map();
    const consumers = new Map();
    const edges = [];

    const addEdge = (from, to, options = {}) => {
      if (!from || !to || from === to || !byStepId.has(from) || !byStepId.has(to)) {
        return;
      }
      const pinKey = options?.targetInputRef ? `::${String(options.targetInputRef)}` : "";
      const key = `${from}->${to}${pinKey}`;
      if (edgeKeys.has(key)) {
        return;
      }
      edgeKeys.add(key);
      edges.push({ from, to, targetInputRef: options?.targetInputRef ?? null });
      if (!predecessors.has(to)) {
        predecessors.set(to, []);
      }
      predecessors.get(to).push(from);
      if (!consumers.has(from)) {
        consumers.set(from, []);
      }
      consumers.get(from).push(to);
    };

    nodeEntries.forEach((entry) => {
      const stepId = entry.stepId;
      (planStepInputsMap.get(stepId) ?? []).forEach((inputStepId) => addEdge(inputStepId, stepId));
      addEdge(planStepParentLookup.get(stepId), stepId);
      const specificInputLinks = Array.isArray(entry?.upstreamInputLinks) ? entry.upstreamInputLinks : EMPTY_ARRAY;
      const inputLinkedStepIds = new Set();
      specificInputLinks.forEach((link) => {
        const sourceStepId = link?.stepId;
        const inputRef = link?.inputRef;
        if (!sourceStepId || !inputRef) {
          return;
        }
        inputLinkedStepIds.add(sourceStepId);
        addEdge(sourceStepId, stepId, { targetInputRef: inputRef });
      });
      (entry?.upstreamStepIds ?? EMPTY_ARRAY).forEach((upstreamStepId) => {
        if (!upstreamStepId || inputLinkedStepIds.has(upstreamStepId)) {
          return;
        }
        addEdge(upstreamStepId, stepId);
      });
    });

    const getPrimaryOutputType = (entry) => {
      const outputs = Array.isArray(entry?.outputs) ? entry.outputs : EMPTY_ARRAY;
      const primary = outputs[0];
      return typeof primary?.type === "string" ? primary.type : null;
    };
    const FEEDER_CORE_TYPES = new Set(["view", "query", "search", "actionrunner", ...DISCOVERY_SOURCE_TYPES]);
    const coreTerminalStepIds = new Set(
      nodeEntries
        .filter((entry) => {
          const stepId = entry?.stepId;
          if (!stepId) {
            return false;
          }
          const primaryType = getPrimaryOutputType(entry);
          return entry?.area === "discovery" || (primaryType && FEEDER_CORE_TYPES.has(primaryType));
        })
        .map((entry) => entry.stepId),
    );
    const coreStepIds = new Set(coreTerminalStepIds);
    const coreStack = Array.from(coreTerminalStepIds);
    while (coreStack.length) {
      const stepId = coreStack.pop();
      const parentStepIds = predecessors.get(stepId) ?? EMPTY_ARRAY;
      parentStepIds.forEach((parentStepId) => {
        if (!parentStepId || coreStepIds.has(parentStepId)) {
          return;
        }
        const parentEntry = byStepId.get(parentStepId);
        if (parentEntry?.forceBand === "leaf") {
          return;
        }
        coreStepIds.add(parentStepId);
        coreStack.push(parentStepId);
      });
    }

    const depthMemo = new Map();
    const computeDepth = (stepId, stack = new Set()) => {
      if (!stepId) return 0;
      if (depthMemo.has(stepId)) return depthMemo.get(stepId);
      if (stack.has(stepId)) return 0;
      stack.add(stepId);
      const parents = predecessors.get(stepId) ?? [];
      const depth = parents.length
        ? Math.max(...parents.map((parentId) => computeDepth(parentId, stack))) + 1
        : 0;
      stack.delete(stepId);
      depthMemo.set(stepId, depth);
      return depth;
    };

    const buckets = new Map();
    nodeEntries.forEach((entry, idx) => {
      const depth = computeDepth(entry.stepId);
      if (!buckets.has(depth)) {
        buckets.set(depth, []);
      }
      buckets.get(depth).push({ entry, idx });
    });

    const NODE_W = 460;
    const NODE_H = 320;
    const COL_GAP = 180;
    const ROW_GAP = 72;
    const PAD_X = 80;
    const PAD_Y = 80;
    const sortedDepths = Array.from(buckets.keys()).sort((a, b) => a - b);
    const nodes = [];

    sortedDepths.forEach((depth) => {
      const col = buckets.get(depth) ?? [];
      col
        .sort((a, b) => {
          const aOrder = Number.isFinite(a.entry?.order) ? a.entry.order : Number.MAX_SAFE_INTEGER;
          const bOrder = Number.isFinite(b.entry?.order) ? b.entry.order : Number.MAX_SAFE_INTEGER;
          return aOrder - bOrder || a.idx - b.idx;
        })
        .forEach(({ entry }, rowIdx) => {
          const stepId = entry.stepId;
          const hasInputs = (predecessors.get(stepId) ?? EMPTY_ARRAY).length > 0;
          const hasConsumers = (consumers.get(stepId) ?? EMPTY_ARRAY).length > 0;
          const isCoreSource = coreStepIds.has(stepId);
          const isTerminalPresentation = entry.area === "presentation" && hasInputs && !hasConsumers;
          const band =
            entry?.forceBand === "leaf"
              ? "leaf"
              : isCoreSource
              ? "core"
              : isTerminalPresentation
                ? "leaf"
                : "interim";
          nodes.push({
            id: `artifact_${entry.stepId}`,
            stepId,
            depth,
            band,
            entry,
            inputRefPins:
              entry?.canvasVariant === "adhoc_summary" && Array.isArray(entry?.upstreamInputLinks)
                ? Array.from(
                    new Set(
                      entry.upstreamInputLinks
                        .map((link) => link?.inputRef)
                        .filter((inputRef) => typeof inputRef === "string" && inputRef.length > 0),
                    ),
                  )
                : EMPTY_ARRAY,
            x: PAD_X + depth * (NODE_W + COL_GAP),
            y: PAD_Y + rowIdx * (NODE_H + ROW_GAP),
            width: Number.isFinite(entry?.preferredWidth) ? entry.preferredWidth : NODE_W,
            height: NODE_H,
          });
        });
    });

    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const frameIdByStepId = new Map(nodes.map((node) => [node.stepId, node.id]));
    const inputRefToPinByNodeId = new Map();
    nodes.forEach((node) => {
      if (!Array.isArray(node?.inputRefPins) || !node.inputRefPins.length) {
        return;
      }
      const pinMap = new Map();
      node.inputRefPins.forEach((inputRef, idx) => {
        pinMap.set(inputRef, 10 + idx);
      });
      inputRefToPinByNodeId.set(node.id, pinMap);
    });
    const routedEdges = edges
      .map((edge) => {
        const source = nodeById.get(frameIdByStepId.get(edge.from));
        const target = nodeById.get(frameIdByStepId.get(edge.to));
        if (!source || !target) return null;
        const rightPinForInputRef = edge?.targetInputRef
          ? inputRefToPinByNodeId.get(target.id)?.get(edge.targetInputRef)
          : undefined;
        return {
          ...edge,
          left: source.id,
          right: target.id,
          leftPin: 2,
          rightPin: rightPinForInputRef ?? 1,
        };
      })
      .filter(Boolean);
    return { nodes, edges: routedEdges };
  }, [entries, planStepInputsMap, planStepParentLookup]);

  const graphLayoutSignature = useMemo(
    () =>
      `${graph.nodes.map((node) => `${node.id}:${node.depth ?? 0}`).join("|")}__${graph.edges
        .map((edge) => `${edge.left}->${edge.right}`)
        .join("|")}`,
    [graph.edges, graph.nodes],
  );
  const [measuredSizesById, setMeasuredSizesById] = useState({});
  const measuredSizesRef = useRef({});
  useEffect(() => {
    measuredSizesRef.current = measuredSizesById;
  }, [measuredSizesById]);
  useEffect(() => {
    setMeasuredSizesById({});
  }, [graphLayoutSignature]);

  useEffect(() => {
    if (!graph.nodes.length) {
      return undefined;
    }
    let cancelled = false;
    const nodeIds = new Set(graph.nodes.map((node) => node.id));
    let rafId = null;
    let samples = 0;
    let stableSamples = 0;
    const MAX_SAMPLES = 240;
    const STABLE_SAMPLE_TARGET = 24;

    const captureSizes = () => {
      if (cancelled) {
        return;
      }
      const frameIds = canvasRef.current?.frameList?.() ?? EMPTY_ARRAY;
      if (!Array.isArray(frameIds) || frameIds.length === 0) {
        samples += 1;
        if (samples < MAX_SAMPLES) {
          rafId = requestAnimationFrame(captureSizes);
        }
        return;
      }

      const prev = measuredSizesRef.current;
      const next = { ...prev };
      let changed = false;
      let matchedCount = 0;

      frameIds.forEach((frameId) => {
        if (!frameId || !nodeIds.has(frameId)) {
          return;
        }
        const frameData = canvasRef.current?.frameData?.(frameId);
        const node = frameData?.node;
        const nodeAttrs = node?.attrs ?? {};
        const nodeScaleX = Number.isFinite(nodeAttrs?.scaleX) ? nodeAttrs.scaleX : 1;
        const nodeScaleY = Number.isFinite(nodeAttrs?.scaleY) ? nodeAttrs.scaleY : nodeScaleX;
        const widthRaw = Number.isFinite(nodeAttrs?.width) ? nodeAttrs.width : 0;
        const heightRaw = Number.isFinite(nodeAttrs?.height) ? nodeAttrs.height : 0;
        const width = Math.max(220, Math.round(widthRaw * nodeScaleX));
        const height = Math.max(180, Math.round(heightRaw * nodeScaleY));
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          return;
        }
        matchedCount += 1;
        const current = prev[frameId];
        if (!current || Math.abs(current.width - width) > 2 || Math.abs(current.height - height) > 2) {
          next[frameId] = { width, height };
          changed = true;
        }
      });

      if (changed) {
        measuredSizesRef.current = next;
        setMeasuredSizesById(next);
        stableSamples = 0;
      } else {
        stableSamples += 1;
      }

      if (matchedCount < nodeIds.size) {
        stableSamples = 0;
      }

      samples += 1;
      if (samples < MAX_SAMPLES && stableSamples < STABLE_SAMPLE_TARGET) {
        rafId = requestAnimationFrame(captureSizes);
      }
    };

    rafId = requestAnimationFrame(captureSizes);
    return () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [graphLayoutSignature, graph.nodes, outputCarouselIndexMap]);

  const framePositions = useMemo(() => {
    const PAD_X = 80;
    const PAD_Y = 80;
    const COL_GAP = 280;
    const ROW_GAP = 112;
    const BAND_GAP = 420;
    const CORE_COL_GAP = 220;
    const PEER_GROUP_GAP = 64;

    const resolveNodeSize = (node) => {
      const measured = measuredSizesById[node.id];
      return {
        width: measured?.width ?? node.width ?? 420,
        height: measured?.height ?? node.height ?? 320,
      };
    };
    const sortByOrder = (a, b) => {
      const aOrder = Number.isFinite(a.entry?.order) ? a.entry.order : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(b.entry?.order) ? b.entry.order : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || String(a.id).localeCompare(String(b.id));
    };

    const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
    const downstream = new Map();
    const upstream = new Map();
    graph.nodes.forEach((node) => {
      downstream.set(node.id, new Set());
      upstream.set(node.id, new Set());
    });
    graph.edges.forEach((edge) => {
      const from = edge?.left;
      const to = edge?.right;
      if (!from || !to || !nodesById.has(from) || !nodesById.has(to) || from === to) {
        return;
      }
      downstream.get(from).add(to);
      upstream.get(to).add(from);
    });

    const coreDepthFromSinkMemo = new Map();
    const computeCoreDepthFromSink = (nodeId, stack = new Set()) => {
      const node = nodesById.get(nodeId);
      if (!node || node.band !== "core") {
        return 0;
      }
      if (coreDepthFromSinkMemo.has(nodeId)) {
        return coreDepthFromSinkMemo.get(nodeId);
      }
      if (stack.has(nodeId)) {
        return 0;
      }
      stack.add(nodeId);
      const coreChildren = Array.from(downstream.get(nodeId) ?? EMPTY_ARRAY).filter((childId) => {
        const childNode = nodesById.get(childId);
        return childNode?.band === "core";
      });
      const depthFromSink = coreChildren.length
        ? Math.max(...coreChildren.map((childId) => computeCoreDepthFromSink(childId, stack))) + 1
        : 0;
      stack.delete(nodeId);
      coreDepthFromSinkMemo.set(nodeId, depthFromSink);
      return depthFromSink;
    };
    const coreNodes = graph.nodes.filter((node) => node?.band === "core");
    coreNodes.forEach((node) => computeCoreDepthFromSink(node.id));
    const coreMaxDepthFromSink = coreNodes.reduce(
      (maxDepth, node) => Math.max(maxDepth, coreDepthFromSinkMemo.get(node.id) ?? 0),
      0,
    );

    const bandOrder = { core: 0, interim: 1, leaf: 2 };
    const columnsMap = new Map();
    graph.nodes.forEach((node) => {
      const baseDepth = Number.isFinite(node?.depth) ? node.depth : 0;
      const depth =
        node?.band === "core"
          ? coreMaxDepthFromSink - (coreDepthFromSinkMemo.get(node.id) ?? 0)
          : baseDepth;
      const band = node?.band ?? "interim";
      const key = band === "leaf" ? "leaf:0" : `${band}:${depth}`;
      if (!columnsMap.has(key)) {
        columnsMap.set(key, {
          key,
          band,
          depth: band === "leaf" ? Number.MAX_SAFE_INTEGER : depth,
          nodes: [],
          width: 320,
          x: PAD_X,
        });
      }
      columnsMap.get(key).nodes.push(node);
    });

    const columns = Array.from(columnsMap.values()).sort((a, b) => {
      const bandDelta = (bandOrder[a.band] ?? 1) - (bandOrder[b.band] ?? 1);
      if (bandDelta !== 0) {
        return bandDelta;
      }
      return a.depth - b.depth;
    });

    columns.forEach((column) => {
      column.nodes.sort(sortByOrder);
      let maxWidth = 320;
      column.nodes.forEach((node) => {
        const { width } = resolveNodeSize(node);
        maxWidth = Math.max(maxWidth, width);
      });
      column.width = maxWidth;
    });

    let cursorX = PAD_X;
    columns.forEach((column, idx) => {
      column.x = cursorX;
      const nextColumn = columns[idx + 1];
      if (!nextColumn) {
        return;
      }
      const sameBand = nextColumn.band === column.band;
      const gap = sameBand ? (column.band === "core" ? CORE_COL_GAP : COL_GAP) : BAND_GAP;
      cursorX += column.width + gap;
    });

    const columnIndexByNodeId = new Map();
    columns.forEach((column, columnIdx) => {
      column.nodes.forEach((node) => columnIndexByNodeId.set(node.id, columnIdx));
    });

    const positions = {};
    const placeColumn = (columnIdx, mode = "rtl") => {
      const column = columns[columnIdx];
      if (!column) {
        return;
      }
      const items = column.nodes.map((node) => {
        const { width, height } = resolveNodeSize(node);
        const x = column.x + Math.max(0, Math.round((column.width - width) / 2));
        const current = positions[node.id];
        const currentY = Number.isFinite(current?.y) ? current.y : PAD_Y;
        const neighbors =
          mode === "rtl"
            ? Array.from(downstream.get(node.id) ?? EMPTY_ARRAY).filter(
                (childId) => (columnIndexByNodeId.get(childId) ?? -1) > columnIdx && positions[childId],
              )
            : Array.from(upstream.get(node.id) ?? EMPTY_ARRAY).filter(
                (parentId) => (columnIndexByNodeId.get(parentId) ?? Number.MAX_SAFE_INTEGER) < columnIdx && positions[parentId],
              );
        const neighborCenters = neighbors
          .map((neighborId) => {
            const neighborNode = nodesById.get(neighborId);
            if (!neighborNode || !positions[neighborId]) {
              return null;
            }
            const neighborSize = resolveNodeSize(neighborNode);
            return positions[neighborId].y + neighborSize.height / 2;
          })
          .filter((value) => Number.isFinite(value));
        const neighborTargetY = neighborCenters.length
          ? neighborCenters.reduce((sum, value) => sum + value, 0) / neighborCenters.length - height / 2
          : currentY;
        const orderFallbackY = PAD_Y + column.nodes.indexOf(node) * (height + ROW_GAP);
        const targetY = Number.isFinite(neighborTargetY) ? neighborTargetY : orderFallbackY;
        const clusterChildren = Array.from(downstream.get(node.id) ?? EMPTY_ARRAY)
          .filter((childId) => (columnIndexByNodeId.get(childId) ?? -1) > columnIdx)
          .sort();
        const peerKey = clusterChildren.length ? clusterChildren.join("|") : `solo:${node.id}`;
        return {
          id: node.id,
          x,
          width,
          height,
          currentY,
          targetY,
          order: Number.isFinite(node?.entry?.order) ? node.entry.order : Number.MAX_SAFE_INTEGER,
          peerKey,
          y: currentY,
        };
      });

      const groups = new Map();
      items.forEach((item) => {
        if (!groups.has(item.peerKey)) {
          groups.set(item.peerKey, []);
        }
        groups.get(item.peerKey).push(item);
      });

      const orderedGroups = Array.from(groups.values())
        .map((groupItems) => {
          groupItems.sort((a, b) => a.targetY - b.targetY || a.order - b.order || a.id.localeCompare(b.id));
          const groupHeight = groupItems.reduce((sum, item, idx) => sum + item.height + (idx > 0 ? ROW_GAP : 0), 0);
          const groupTargetCenter =
            groupItems.reduce((sum, item) => sum + (item.targetY + item.height / 2), 0) / Math.max(1, groupItems.length);
          return {
            items: groupItems,
            groupHeight,
            groupTargetTop: groupTargetCenter - groupHeight / 2,
          };
        })
        .sort((a, b) => a.groupTargetTop - b.groupTargetTop);

      let runningY = PAD_Y;
      orderedGroups.forEach((group, groupIdx) => {
        const desiredTop = Math.round(group.groupTargetTop);
        const top = Math.max(runningY, desiredTop);
        let itemY = top;
        group.items.forEach((item) => {
          item.y = itemY;
          itemY += item.height + ROW_GAP;
        });
        runningY = itemY + (groupIdx < orderedGroups.length - 1 ? PEER_GROUP_GAP : 0);
      });

      items
        .sort((a, b) => a.y - b.y || a.order - b.order || a.id.localeCompare(b.id))
        .forEach((item, idx) => {
          const prev = idx > 0 ? items[idx - 1] : null;
          const minY = prev ? prev.y + prev.height + ROW_GAP : PAD_Y;
          item.y = Math.max(item.y, minY);
          positions[item.id] = {
            x: item.x,
            y: Math.round(item.y),
            s: 1,
          };
        });
    };

    columns.forEach((column, columnIdx) => {
      placeColumn(columnIdx, "rtl");
    });
    for (let pass = 0; pass < 4; pass += 1) {
      for (let columnIdx = columns.length - 1; columnIdx >= 0; columnIdx -= 1) {
        placeColumn(columnIdx, "rtl");
      }
      for (let columnIdx = 0; columnIdx < columns.length; columnIdx += 1) {
        placeColumn(columnIdx, "ltr");
      }
    }

    return positions;
  }, [graph.edges, graph.nodes, measuredSizesById]);
  const layoutUpdateKey = useMemo(() => {
    const sizeSig = Object.keys(measuredSizesById)
      .sort()
      .map((id) => {
        const size = measuredSizesById[id];
        return `${id}:${size?.width ?? 0}x${size?.height ?? 0}`;
      })
      .join("|");
    return `${graphLayoutSignature}__${sizeSig}`;
  }, [graphLayoutSignature, measuredSizesById]);

  const frameDataById = useMemo(() => {
    const map = new Map();
    graph.nodes.forEach((node) => {
      const entry = node.entry;
      const outputs = entry.outputs ?? [];
      const currentIndex = outputCarouselIndexMap[entry.stepId] ?? 0;
      const activePrimitive = outputs[currentIndex] ?? outputs[0] ?? null;
      map.set(node.id, { entry, primitive: activePrimitive });
    });
    return map;
  }, [graph.nodes, outputCarouselIndexMap]);
  frameDataByIdRef.current = frameDataById;

  const renderSet = useMemo(() => {
    const getPinY = (nodeId, fallbackHeight = 320) => {
      const measuredHeight = measuredSizesById?.[nodeId]?.height;
      const resolvedHeight = Number.isFinite(measuredHeight) ? measuredHeight : fallbackHeight;
      return Math.max(24, Math.round(resolvedHeight / 2));
    };
    const buildInputPins = (node, fallbackHeight = 320) => {
      const pinRefs = Array.isArray(node?.inputRefPins) ? node.inputRefPins : EMPTY_ARRAY;
      if (!pinRefs.length) {
        return [{ idx: 1, y: getPinY(node?.id, fallbackHeight), internal: false }];
      }
      const measuredHeight = measuredSizesById?.[node?.id]?.height;
      const resolvedHeight = Number.isFinite(measuredHeight) ? measuredHeight : fallbackHeight;
      const denominator = pinRefs.length + 1;
      return pinRefs.map((_, idx) => ({
        idx: 10 + idx,
        y: Math.max(24, Math.round(((idx + 1) * resolvedHeight) / denominator)),
        internal: false,
      }));
    };
    const buildFallbackFrame = (node, title, text, fallbackHeight = 220) => ({
      id: node?.id,
      title: () => title,
      canChangeSize: true,
      canvasMargin: [8, 8, 8, 8],
      pins: {
        input: buildInputPins(node, fallbackHeight),
        output: [{ idx: 2, y: getPinY(node?.id, fallbackHeight), internal: false }],
      },
      items: (stageOptions = {}) =>
        renderPlainObject({
          id: node?.id,
          type: "text",
          text,
          width: stageOptions?.width ?? 360,
          height: stageOptions?.height ?? 220,
          padding: [16, 16, 16, 16],
          ...stageOptions,
        }),
    });

    return graph.nodes.map((node) => {
      const payload = frameDataById.get(node.id);
      const activePrimitive = payload?.primitive ?? null;
      const hasRenderablePrimitive =
        Boolean(activePrimitive) &&
        typeof activePrimitive === "object" &&
        typeof activePrimitive.type === "string" &&
        typeof activePrimitive.id !== "undefined";
      const fallbackTitle = node.entry?.name ?? activePrimitive?.title ?? "Output";
      if (!hasRenderablePrimitive) {
        const fallbackText =
          typeof activePrimitive === "string"
            ? activePrimitive
            : activePrimitive && typeof activePrimitive === "object"
              ? "Output preview unavailable for this artefact type."
              : "Output not available yet.";
        return buildFallbackFrame(node, fallbackTitle, fallbackText, node?.height ?? 220);
      }
      let boardView;
      try {
        const forcedWidth =
          payload?.entry?.canvasVariant === "adhoc_summary" ? ARTEFACTS_CANVAS_SUMMARY_WIDTH : undefined;
        BoardViewer.prepareBoard(
          activePrimitive,
          boardStateRef.current,
          undefined,
          forcedWidth ? { width: forcedWidth } : undefined,
        );
        boardView = BoardViewer.renderBoardView(
          activePrimitive,
          activePrimitive,
          boardStateRef.current,
          {
            theme: activePrimitive?.renderConfig?.theme ?? "default",
            ...(forcedWidth ? { width: forcedWidth } : {}),
          },
        );
      } catch (error) {
        return buildFallbackFrame(
          node,
          fallbackTitle,
          "Output preview unavailable for this artefact type.",
          node?.height ?? 220,
        );
      }
      if (!boardView || typeof boardView !== "object" || typeof boardView.items !== "function") {
        return buildFallbackFrame(
          node,
          fallbackTitle,
          "Output preview unavailable for this artefact type.",
          node?.height ?? 220,
        );
      }
      return {
        ...(boardView ?? {}),
        id: node.id,
        title: () => fallbackTitle,
        canChangeSize: true,
        canvasMargin: [8, 8, 8, 8],
        pins: {
          input: buildInputPins(node, node?.height ?? 320),
          output: [{ idx: 2, y: getPinY(node.id, node?.height ?? 320), internal: false }],
        },
      };
    });
  }, [graph.nodes, frameDataById, measuredSizesById]);

  const selectItems = useCallback(
    (primitiveOrId) => {
      const primitive =
        typeof primitiveOrId === "string" ? mainstore.primitive(primitiveOrId) : primitiveOrId;
      if (primitive) {
        onOpenWorkspaceRef.current?.(primitive);
      }
    },
    [mainstore],
  );

  const canvasCallbacks = useMemo(() => {
    const base = buildCanvasClickHandlers({
      mainstore,
      selectItems,
      uniquePrimitives,
    });
    return {
      ...base,
      onClick: {
        ...(base.onClick ?? {}),
        frame: (id) => {
          const payload = frameDataByIdRef.current.get(id);
          if (payload?.entry && payload?.primitive) {
            onCardSelectRef.current?.(payload.entry, payload.primitive);
            return;
          }
          base.onClick?.frame?.(id);
        },
      },
    };
  }, [mainstore, selectItems]);

  if (!graph.nodes.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        No artefacts available yet.
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full min-h-[34rem] min-w-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
      style={{ contain: "layout paint size", isolation: "isolate" }}
    >
      <InfiniteCanvas
        ref={canvasRef}
        update={layoutUpdateKey}
        primitive={{ id: "artefacts_canvas" }}
        render={renderSet}
        framePositions={framePositions}
        frameLinks={graph.edges}
        board
        hideWidgets={true}
        background="inherit"
        ignoreAfterDrag={false}
        showPins={false}
        events={{
          wheel: {
            passive: false,
          },
        }}
        enableShapeSelection={false}
        enableFrameSelection={true}
        enableZoom={true}
        enableDrag={false}
        initialZoom="full"
        callbacks={canvasCallbacks}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.mainstore !== nextProps.mainstore) {
    return false;
  }
  if (!areCanvasEntriesEqual(prevProps.entries, nextProps.entries)) {
    return false;
  }
  const stepIds = (nextProps.entries ?? EMPTY_ARRAY).map((entry) => entry?.stepId).filter(Boolean);
  for (const stepId of stepIds) {
    if ((prevProps.outputCarouselIndexMap?.[stepId] ?? 0) !== (nextProps.outputCarouselIndexMap?.[stepId] ?? 0)) {
      return false;
    }
  }
  if (!areStepDependencyMapsEqual(prevProps, nextProps)) {
    return false;
  }
  return true;
});

function SelectedOutputDetail({
  entry,
  primitive,
  planStepIndexLookup,
  onBack,
  onOpenWorkspace,
  viewportWidth,
  stepPrimitive,
  stepSnapshot,
}) {
  const contentHints = resolveStepContentHints({ stepSnapshot, stepPrimitive });
  const contentMarkdown = buildContentMarkdownTable(primitive?.itemsForProcessing, contentHints);
  const storedOutputSelection =
    typeof stepPrimitive?.referenceParameters?.outputSelection === "string"
      ? stepPrimitive.referenceParameters.outputSelection
      : "Outputs";
  const [selectedOutputName, setSelectedOutputName] = useState(storedOutputSelection);

  const flowOutputNames = useMemo(() => {
    if (primitive?.type !== "flowinstance") {
      return [];
    }
    const pins = primitive.origin?.outputPins ?? {};
    const deduped = [];
    const seen = new Set();
    Object.values(pins).forEach((pin) => {
      const name = typeof pin?.name === "string" ? pin.name.trim() : "";
      if (!name) {
        return;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      deduped.push(name);
    });
    return deduped;
  }, [primitive?.id, primitive?.origin?.outputPins]);

  const showOutputSelector = primitive?.type === "flowinstance" && flowOutputNames.length > 0;

  useEffect(() => {
    if (!showOutputSelector) {
      setSelectedOutputName("");
      return;
    }
    setSelectedOutputName((prev) => {
      const sanitizedPrev = prev && flowOutputNames.includes(prev) ? prev : "";
      if (storedOutputSelection) {
        return storedOutputSelection;
      }
      return sanitizedPrev;
    });
  }, [
    showOutputSelector,
    flowOutputNames,
    storedOutputSelection,
    stepPrimitive?.id,
    primitive?.id,
  ]);

  const handleOutputSelectionChange = useCallback(
    (nextValue) => {
      setSelectedOutputName(nextValue);
      if (!stepPrimitive?.setField) {
        return;
      }
      const currentStored =
        typeof stepPrimitive.referenceParameters?.outputSelection === "string"
          ? stepPrimitive.referenceParameters.outputSelection
          : "";
      if ((nextValue || "") === currentStored) {
        return;
      }
      stepPrimitive.setField("referenceParameters.outputSelection", nextValue || null);
    },
    [stepPrimitive],
  );

  const renderPrimitiveContent = () => {
    if (!primitive) {
      return (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
          Output not available yet.
        </div>
      );
    }

    if (primitive.type === "flowinstance") {
      const outputNamesProp = selectedOutputName ? [selectedOutputName] : [];
      return (
        <div className="flex h-full min-h-[400px] w-full flex-col gap-3">
          {showOutputSelector && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Output</div>
              <select
                value={selectedOutputName}
                onChange={(event) => handleOutputSelectionChange(event.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-700 focus:border-blue-400 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              >
                <option value="">output</option>
                {flowOutputNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex h-full min-h-0 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <FlowInstanceOutput
              key={`${primitive.id}_${outputNamesProp?.[0] ?? "all"}`}
              primitive={primitive}
              background="inherit"
              initialZoom="full"
              spinnerWhenRunning={true}
              enableDrag={false}
              enableTextSelection={true}
              enableFrameSelection="overlay"
              hideProgressAt="@4xl"
              hideEmptyPages={true}
              outputNames={outputNamesProp}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-[400px] w-full">
        <div className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex-1 min-h-0">
            {contentMarkdown ? (
              <div className="flex h-full min-h-0 w-full items-start overflow-auto">
                <MarkdownTablePreview
                  markdown={contentMarkdown}
                  width={Math.max(480, viewportWidth ? Math.floor(viewportWidth * 0.42) : 640)}
                  className="w-full"
                  scaleToFit={false}
                />
              </div>
            ) : (
              <KonvaPrimitive primitive={primitive} enableZoom={true} enableDrag={false} enableTextSelection={true} />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <SelectedOutputHeader
        entry={entry}
        planStepIndexLookup={planStepIndexLookup}
        onBack={onBack}
        onOpenWorkspace={onOpenWorkspace}
      />
      <div className="flex-1 overflow-auto bg-slate-50 p-6 dark:bg-slate-950">
        {renderPrimitiveContent()}
      </div>
    </div>
  );
}
